import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, CircularProgress, Grid } from "@mui/material";
import { StyledComponentGroup, StyledProductsWrapper } from "./styled";
import ReplayIcon from "@mui/icons-material/Replay";
import RepeatIcon from "@mui/icons-material/Repeat";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import Reason, { clearReasonDraft } from "./reason";
import SameProduct from "./SameProduct";
import DifferentProduct from "./DifferentProduct";
import BottomActionBar from "components/v2/Common/BottomActionBar";
import CheckmarkControl from "components/v2/Common/CheckmarkControl";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getEncipherencode } from "utils/encipherencode";
import { getWarrantyClaimParam, getClaimRemainingQuantity } from "utils/warrantyClaim";
import { getEffectiveResolutionConfig } from "utils/reasonResolution";
import styled from "styled-components";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  getSelectedOrder,
  updateReturnOrder,
  updateSelectedOrder,
  updateUUID,
} from "features/orders/orderSlice";
import { ordersList, selectedOrderAPI } from "utils/api";
import {
  addCustomerComment,
  getCustomerComment,
  addSelectedProduct,
  addProductid,
  getProductid,
  getProductType,
  getReplacementMode,
  getSelectedProduct,
  getSelectedReason,
  getSelectedSubReason,
  getUploadMediaIds,
  addSelectedReason,
  addSelectedSubReason,
  setImageUploads,
  addPreviews,
  addUploads,
  addUploadMediaIds,
  addProductType,
  addProceedBool,
  addSelectedGroupProducts,
  setReplacementMode,
} from "features/product/productSlice";
import {
  addRefundJSON,
  addRefundSource,
  addReturnedProducts,
  getRefundJSON,
  getRefundSource,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import { getEmail, getPhone } from "features/user/userSlice";
import { LazyLoadImage } from "react-lazy-load-image-component";
import LoadingError from "components/v2/Common/LoadingError";
import LineItemCard from "components/v2/Common/LineItemCard";
import {
  addAddress,
  addShipmentMethod,
  getAddress,
  getShipmentMethod,
  updateServiceability,
} from "features/address/addresSlice";
import { getOrderPickupAddress } from "utils/returnAddress";
import {
  clearReturnCartContext,
  getReturnCartContext,
  saveReturnCartContext,
} from "utils/returnCartSession";
import {
  hasValidTrackingReference,
  stripVersionPrefix,
  versionedPath,
} from "utils/routes";
import { restorePersistedReturnMediaUpload } from "utils/returnMedia";
import { isLineItemReplacementEligible } from "utils/replacementEligibility";
import { V2ProductsBoneyardLoader } from "components/v2/Loaders/BoneyardLoaders";
import PageHeader from "components/v2/Common/PageHeader";
import PageIntro from "components/v2/Common/PageIntro";
import FloatingCloseButton from "components/v2/Common/FloatingCloseButton";
import BottomSheetDrawer from "components/v2/Common/BottomSheetDrawer";
import {
  formatRequestWindow,
  getAggregatedRequestWindows,
  resolveRequestWindows,
} from "utils/requestWindows";
import { getReturnFlowDeepLink } from "utils/returnFlowDeepLink";
import { toast } from "react-toastify";
// import cartImage from "assets/cart.svg";

// Keep the wrapper identity stable when questionnaire validity updates the page.
const StyledApp = styled.div``;

function ProductsDisplay() {
  const merchantConfig = useSelector(getMerchantConfig);
  const defaultSelectedOrder = useSelector(getSelectedOrder);
  const returnedProducts = useSelector(getReturnedProducts);
  const rmsConfig = useSelector(getRmsConfig);
  const userPhone = useSelector(getPhone);
  const userEmail = useSelector(getEmail);
  const defaultProductid = useSelector(getProductid);
  const customerComment = useSelector(getCustomerComment);
  const selectedProductState = useSelector(getSelectedProduct);
  const selectedReasonState = useSelector(getSelectedReason);
  const selectedSubReasonState = useSelector(getSelectedSubReason);
  const uploadMediaIds = useSelector(getUploadMediaIds);
  const selectedProductType = useSelector(getProductType);
  const isReplacement = useSelector(getReplacementMode);
  const addressDetails = useSelector(getAddress);
  const shipmentMethod = useSelector(getShipmentMethod);
  const refundSource = useSelector(getRefundSource);
  const refundJSON = useSelector(getRefundJSON);

  const [productid, setProductid] = useState(defaultProductid);
  const dispatch = useDispatch();
  const [selectedOrder, setSelectedOrder] = useState(defaultSelectedOrder);
  const [productsLoading, setProductsLoading] = useState(true);
  const [apiErrorLoading, setApiErrorLoading] = useState(false);
  const [incentiviseExchangeModalOpen, setIncentiviseExchangeModalOpen] =
    useState(false);
  const [pendingIncentiviseSelection, setPendingIncentiviseSelection] =
    useState(null);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [updatedReturnedProducts, setUpdatedReturnedProducts] =
    useState(returnedProducts);
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const [activeGroupAction, setActiveGroupAction] = useState(null);
  const [isReplacementAction, setIsReplacementAction] = useState(false);
  const [activeLineAction, setActiveLineAction] = useState(null);
  const [activeLineItemId, setActiveLineItemId] = useState(null);
  const [canContinue, setCanContinue] = useState(false);
  const [reasonSubmitting, setReasonSubmitting] = useState(false);
  const [reasonValidationAttempted, setReasonValidationAttempted] =
    useState(false);
  const [reasonButtonErrorShake, setReasonButtonErrorShake] = useState(false);
  const [selectedLineCount, setSelectedLineCount] = useState(0);
  const [exchangeSelectedVariant, setExchangeSelectedVariant] = useState(null);
  const [exchangeRequiredProduct, setExchangeRequiredProduct] = useState(null);
  const [exchangeOpenFilter, setExchangeOpenFilter] = useState(false);
  const productTagConsentText =
    rmsConfig.product_tag_consent && rmsConfig.product_tag_consent_text
      ? rmsConfig.product_tag_consent_text
      : "";
  const sameExchangeSubmitRef = useRef(null);
  const reasonSubmitRef = useRef(null);
  const hasAppliedReturnFlowDeepLinkRef = useRef(false);
  const activeLineItemQuantityRef = useRef({});
  const prevExchangeLineItemRef = useRef(
    selectedProductState?.line_item_id || null,
  );

  const closeReasonFlow = useCallback(() => {
    reasonSubmitRef.current = null;
  }, []);

  const resetReasonValidationFeedback = useCallback(() => {
    setReasonValidationAttempted(false);
    setReasonButtonErrorShake(false);
  }, []);

  const getReasonDraftKey = useCallback(
    (lineItemId) =>
      selectedOrder?.order_id && lineItemId !== undefined && lineItemId !== null
        ? `${selectedOrder.order_id}:${lineItemId}`
        : lineItemId,
    [selectedOrder?.order_id],
  );

  const clearLineItemReasonDraft = useCallback(
    (lineItemId) => {
      if (lineItemId === undefined || lineItemId === null) {
        return;
      }

      clearReasonDraft(getReasonDraftKey(lineItemId));
      dispatch(
        addUploadMediaIds(
          (uploadMediaIds || []).filter(
            (item) => String(item.line_item_id) !== String(lineItemId),
          ),
        ),
      );
    },
    [dispatch, getReasonDraftKey, uploadMediaIds],
  );

  const resetReasonDraft = useCallback(() => {
    closeReasonFlow();
    dispatch(addSelectedReason(null));
    dispatch(addSelectedSubReason(null));
    dispatch(addCustomerComment(null));
    dispatch(setImageUploads([]));
    dispatch(addPreviews([]));
    dispatch(addUploads([]));
    dispatch(addProductType(null));
    dispatch(addProceedBool(false));
    dispatch(setReplacementMode(false));
    dispatch(addProductid(null));
    setProductid(null);
  }, [closeReasonFlow, dispatch]);

  const resetSelectionForNewGroup = useCallback(() => {
    resetReasonDraft();
    setIncentiviseExchangeModalOpen(false);
    setPendingIncentiviseSelection(null);
  }, [resetReasonDraft]);

  const createParentFromChild = (child) => {
    if (!child) {
      return null;
    }

    const parentPrice =
      child.parent_product_price !== null &&
      child.parent_product_price !== undefined
        ? child.parent_product_price
        : child.product_price;

    return {
      line_item_id: child.parent_item_id,
      product_title: child.parent_title || child.product_title,
      variant_name: child.parent_variant || "",
      sku: child.parent_product_sku || "",
      quantity: child.parent_quantity || child.quantity,
      remaining_qty: child.parent_quantity || child.quantity,
      product_price: parentPrice,
      discounted_price: parentPrice,
      original_product_price: parentPrice,
      total_item_discount: 0,
      parent_image_url: child.parent_image_url,
      image_url: child.parent_image_url || child.image_url,
    };
  };

  const dedupeErrors = (errors = []) => {
    if (!errors || errors.length === 0) {
      return [];
    }

    const uniqueErrors = [];
    const seen = new Set();

    errors.forEach((error) => {
      if (!error) {
        return;
      }

      const key = `${error.error_code || ""}-${error.error_code_message || ""}`;

      if (!seen.has(key)) {
        seen.add(key);
        uniqueErrors.push(error);
      }
    });

    return uniqueErrors;
  };

  const normalizeErrors = (errors = []) => {
    const uniqueErrors = dedupeErrors(errors);
    return uniqueErrors.length > 0 ? [uniqueErrors[0]] : [];
  };

  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const deepLinkOrderId = params.get("oid");
  const requiresDeepLinkOrderBootstrap = Boolean(
    deepLinkOrderId &&
      String(selectedOrder?.order_id) !== String(deepLinkOrderId),
  );
  const returnFlowDeepLink = useMemo(
    () => getReturnFlowDeepLink(params),
    [params],
  );
  const warrantyClaimParam = getWarrantyClaimParam(params);
  const isWarrantyClaimFlow =
    warrantyClaimParam?.toLowerCase() === "true" ||
    returnFlowDeepLink?.returnType === "warranty";

  const getLineItemReferenceId = useCallback((product) =>
    product?.reference_id ||
    product?.return_reference_id ||
    product?.return_request_reference_id ||
    product?.request_reference_id ||
    product?.tracking_reference_id ||
    null, []);

  const getRemainingQuantity = useCallback((item) =>
    getClaimRemainingQuantity(item, isWarrantyClaimFlow), [isWarrantyClaimFlow]);

  const isRequestAlreadyCreatedItem = useCallback((item) =>
    getRemainingQuantity(item) <= 0, [getRemainingQuantity]);

  const isReplacementAvailable = useCallback(
    (item) =>
      Boolean(
        rmsConfig.supports_replacement &&
          isLineItemReplacementEligible(item),
      ),
    [rmsConfig.supports_replacement],
  );

  const customerCanExchange = Boolean(
    !rmsConfig.only_replacement &&
      (rmsConfig.supports_exchange || selectedOrder?.force_exchange),
  );

  const isSelectableOrderItem = useCallback(
    (item) =>
      Boolean(item?.visible) &&
      (isWarrantyClaimFlow ? item?.warranty_claim_eligible === true : Boolean(item?.eligible || isReplacementAvailable(item))) &&
      getRemainingQuantity(item) > 0,
    [getRemainingQuantity, isReplacementAvailable, isWarrantyClaimFlow],
  );

  const getOrderItemDisplayRank = useCallback((item) => {
    if (isSelectableOrderItem(item)) {
      return 0;
    }

    if (isRequestAlreadyCreatedItem(item) || getLineItemReferenceId(item)) {
      return 1;
    }

    return 2;
  }, [
    getLineItemReferenceId,
    isRequestAlreadyCreatedItem,
    isSelectableOrderItem,
  ]);

  const compareOrderItemsForDisplay = useCallback((left, right) => {
    const rankDiff =
      getOrderItemDisplayRank(left) - getOrderItemDisplayRank(right);

    if (rankDiff !== 0) {
      return rankDiff;
    }

    return 0;
  }, [getOrderItemDisplayRank]);

  const groupedOrderItems = useMemo(() => {
    const items = [...(selectedOrder?.order_items || [])].sort(
      compareOrderItemsForDisplay,
    );
    const map = new Map();
    const orderKeys = [];

    const getGroupKey = (item) => {
      if (item.sku_type === "bundle") {
        if (
          // item.wms &&
          // item.wms?.toLowerCase() === "easyecom" &&
          item.parent_item_id
        ) {
          return `wms-${item.parent_item_id}`;
        }

        if (item.wms_item_id) {
          return `wms-${item.wms_item_id}`;
        }
      }

      return `single-${item.line_item_id}`;
    };

    items.forEach((item) => {
      const key = getGroupKey(item);

      if (!map.has(key)) {
        map.set(key, []);
        orderKeys.push(key);
      }

      map.get(key).push(item);
    });

    return orderKeys.map((key) => {
      const members = [...(map.get(key) || [])].sort(
        compareOrderItemsForDisplay,
      );

      const isGroupKey = key.startsWith("wms-") || key.startsWith("parent-");

      if (!isGroupKey) {
        return { type: "single", item: members[0] };
      }

      const parentCandidate = members.find((member) => member.is_parent);
      const children = parentCandidate
        ? members.filter(
            (member) => member.line_item_id !== parentCandidate.line_item_id,
          )
        : members;

      if (!children || children.length === 0) {
        return { type: "single", item: parentCandidate || members[0] };
      }

      const parentSource =
        parentCandidate ||
        children.find(
          (child) =>
            child.parent_title ||
            child.parent_variant ||
            child.parent_product_sku ||
            child.parent_image_url,
        ) ||
        children[0];

      const derivedParent =
        parentCandidate || createParentFromChild(parentSource);

      if (!derivedParent) {
        return { type: "single", item: children[0] };
      }

      const parentDetails = parentCandidate
        ? { ...derivedParent }
        : derivedParent;

      return {
        type: "group",
        key,
        parent: parentDetails,
        children,
      };
    });
  }, [compareOrderItemsForDisplay, selectedOrder?.order_items]);

  const aggregateGroupProduct = (parent, children) => {
    if (!children || children.length === 0) {
      return parent || {};
    }

    const some = (callback) => children.some((child) => callback(child));
    const every = (callback) =>
      children.length > 0 && children.every((child) => callback(child));

    const aggregated = {
      ...parent,
    };

    aggregated.is_delivered =
      parent?.is_delivered !== undefined && parent?.is_delivered !== null
        ? parent.is_delivered
        : some((child) => child.is_delivered);

    aggregated.delivery_date_expired =
      parent?.delivery_date_expired !== undefined &&
      parent?.delivery_date_expired !== null
        ? parent.delivery_date_expired
        : every((child) => child.delivery_date_expired);

    aggregated.eligible =
      parent?.eligible !== undefined
        ? parent.eligible
        : some((child) => child.eligible);

    aggregated.remaining_qty =
      parent?.remaining_qty !== undefined && parent?.remaining_qty !== null
        ? parent.remaining_qty
        : children.reduce((total, child) => {
            if (
              child.remaining_qty !== undefined &&
              child.remaining_qty !== null
            ) {
              return total + child.remaining_qty;
            }
            if (child.quantity !== undefined && child.quantity !== null) {
              return total + child.quantity;
            }
            return total;
          }, 0);

    aggregated.returned_qty =
      parent?.returned_qty !== undefined && parent?.returned_qty !== null
        ? parent.returned_qty
        : children.reduce(
            (total, child) => total + (child.returned_qty || 0),
            0,
          );

    aggregated.reference_id =
      getLineItemReferenceId(parent) ||
      children.map((child) => getLineItemReferenceId(child)).find(Boolean) ||
      null;

    aggregated.refund_eligible =
      parent?.refund_eligible !== undefined && parent?.refund_eligible !== null
        ? parent.refund_eligible
        : some((child) => child.refund_eligible);

    aggregated.exchange_eligible =
      parent?.exchange_eligible !== undefined &&
      parent?.exchange_eligible !== null
        ? parent.exchange_eligible
        : some((child) => child.exchange_eligible);

    aggregated.replacement_eligible =
      parent?.replacement_eligible !== undefined &&
      parent?.replacement_eligible !== null
        ? parent.replacement_eligible
        : some((child) => isLineItemReplacementEligible(child));
    aggregated.eligible =
      aggregated.eligible || isReplacementAvailable(aggregated);

    aggregated.exchange_date_expired =
      parent?.exchange_date_expired !== undefined &&
      parent?.exchange_date_expired !== null
        ? parent.exchange_date_expired
        : every((child) => child.exchange_date_expired);

    aggregated.return_date_expired =
      parent?.return_date_expired !== undefined &&
      parent?.return_date_expired !== null
        ? parent.return_date_expired
        : every((child) => child.return_date_expired);

    Object.assign(
      aggregated,
      getAggregatedRequestWindows(parent, children),
    );

    aggregated.refund_restrict_msg =
      parent?.refund_restrict_msg !== undefined &&
      parent?.refund_restrict_msg !== null
        ? parent.refund_restrict_msg
        : children.find((child) => child.refund_restrict_msg)
            ?.refund_restrict_msg ||
          parent?.refund_restrict_msg ||
          null;

    aggregated.exchange_restrict_msg =
      parent?.exchange_restrict_msg !== undefined &&
      parent?.exchange_restrict_msg !== null
        ? parent.exchange_restrict_msg
        : children.find((child) => child.exchange_restrict_msg)
            ?.exchange_restrict_msg ||
          parent?.exchange_restrict_msg ||
          null;

    const parentErrors = normalizeErrors(parent?.errors || []);

    const hasEligibleChild = some((child) => {
      if (!child) {
        return false;
      }

      const remainingQuantity =
        child.remaining_qty !== undefined && child.remaining_qty !== null
          ? child.remaining_qty
          : child.quantity;

      if (!remainingQuantity || remainingQuantity <= 0) {
        return false;
      }

      if (!child.is_delivered || child.is_delivered === null) {
        return false;
      }

      if (child.delivery_date_expired) {
        return false;
      }

      const canReturn = !child.return_date_expired && child.refund_eligible;

      const canExchange =
        !child.exchange_date_expired && child.exchange_eligible;

      const canReplace = isReplacementAvailable(child);

      return canReturn || canExchange || canReplace;
    });

    const childErrors = normalizeErrors(
      children.flatMap((child) => child.errors || []),
    );

    aggregated.errors =
      parentErrors.length > 0
        ? parentErrors
        : hasEligibleChild
          ? []
          : childErrors;

    return aggregated;
  };

  const getChildActionState = (child, actionType, isReplacement = false) => {
    if (!child || !actionType) {
      return { selectable: false, message: null };
    }

    if (!child.is_delivered || child.is_delivered === null) {
      return {
        selectable: false,
        message: "The item is yet to be delivered",
      };
    }

    if (child.delivery_date_expired) {
      return {
        selectable: false,
        message: `${
          rmsConfig.only_replacement
            ? "Replacement"
            : rmsConfig.supports_exchange && rmsConfig.supports_refund
              ? "Return/Exchange"
              : rmsConfig.supports_exchange
                ? "Exchange"
                : "Return"
        } period is expired`,
      };
    }

    const remainingQuantity =
      child.remaining_qty !== undefined && child.remaining_qty !== null
        ? child.remaining_qty
        : child.quantity;

    if (!remainingQuantity || remainingQuantity <= 0) {
      const referenceId = getLineItemReferenceId(child);
      return {
        selectable: false,
        referenceId,
        message: `You've already placed a ${
          rmsConfig.only_replacement ? "replace" : "return/exchange"
        } for this item.${
          referenceId
            ? ""
            : " Please reach out to us by clicking on the Need Help button at the bottom of the page for further assistance."
        }`,
      };
    }

    if (actionType === "return") {
      if (child.return_date_expired) {
        return {
          selectable: false,
          message: "Return period is expired for this item",
        };
      }

      if (!child.refund_eligible) {
        return {
          selectable: false,
          message:
            child.refund_restrict_msg ||
            "This item is not eligible for return",
        };
      }
    }

    if (actionType === "exchange") {
      if (!isReplacement && child.exchange_date_expired) {
        return {
          selectable: false,
          message: "Exchange period is expired for this item",
        };
      }

      if (isReplacement) {
        if (!isReplacementAvailable(child)) {
          return {
            selectable: false,
            message: "Replacement is not available for this item.",
          };
        }
        return { selectable: true, message: null };
      } else if (!child.exchange_eligible) {
        return {
          selectable: false,
          message:
            child.exchange_restrict_msg ||
            "This item is not eligible for exchange.",
        };
      }
    }

    if (actionType === "reason_first") {
      return {
        selectable: Boolean(
          (rmsConfig.supports_refund && !child.return_date_expired && child.refund_eligible) ||
          (customerCanExchange && !child.exchange_date_expired && child.exchange_eligible) ||
          isReplacementAvailable(child),
        ),
        message: "This item has no available request options.",
      };
    }

    // if (child.errors?.length > 0) {
    //   return {
    //     selectable: false,
    //     message: child.errors[0].error_code_message,
    //   };
    // }

    return { selectable: true, message: null };
  };

  const handleGroupActionSelection = useCallback(
    (groupKey, actionType, options = {}) => {
      if (!groupKey || !actionType) {
        return;
      }

      const normalizedAction = actionType;
      const replacementSelected = Boolean(options.isReplacement);
      const isSameGroup = expandedGroupKey === groupKey;
      const isSameSelection =
        isSameGroup &&
        activeGroupAction === normalizedAction &&
        isReplacementAction === replacementSelected;

      if (isSameSelection) {
        resetSelectionForNewGroup();
        setExpandedGroupKey(null);
        setActiveGroupAction(null);
        setIsReplacementAction(false);
        return;
      }

      const selectionChanged =
        !isSameGroup ||
        activeGroupAction !== normalizedAction ||
        isReplacementAction !== replacementSelected;

      if (selectionChanged) {
        resetSelectionForNewGroup();
      }

      setExpandedGroupKey(groupKey);
      setActiveGroupAction(normalizedAction);
      setIsReplacementAction(replacementSelected);
    },
    [
      activeGroupAction,
      expandedGroupKey,
      isReplacementAction,
      resetSelectionForNewGroup,
    ],
  );

  const isRequestAlreadyCreated = useCallback(
    (product) => isRequestAlreadyCreatedItem(product),
    [isRequestAlreadyCreatedItem],
  );

  const hasSelectableEntry = useCallback((entry) => {
    if (entry.type === "single") {
      return isSelectableOrderItem(entry.item);
    }

    return entry.children.some((child) => isSelectableOrderItem(child));
  }, [isSelectableOrderItem]);

  const eligibleDisplayItems = useMemo(
    () =>
      groupedOrderItems.filter((entry) => hasSelectableEntry(entry)),
    [groupedOrderItems, hasSelectableEntry],
  );

  const ineligibleDisplayItems = useMemo(
    () =>
      groupedOrderItems.filter((entry) => !hasSelectableEntry(entry)),
    [groupedOrderItems, hasSelectableEntry],
  );

  const getTrackableRequestReferenceId = useCallback(
    (item) => {
      if (!item || !isRequestAlreadyCreatedItem(item)) {
        return null;
      }

      const referenceId = getLineItemReferenceId(item);
      if (!referenceId) {
        return null;
      }

      const normalizedReferenceId = String(referenceId).trim();
      return normalizedReferenceId || null;
    },
    [getLineItemReferenceId, isRequestAlreadyCreatedItem],
  );

  const requestGroupedIneligibleItems = useMemo(() => {
    const requestGroups = new Map();
    const displayEntries = [];

    const addRequestItem = (item, referenceId) => {
      if (!requestGroups.has(referenceId)) {
        const requestGroup = {
          type: "request-reference-group",
          key: `request-reference-${referenceId}`,
          referenceId,
          items: [],
        };

        requestGroups.set(referenceId, requestGroup);
        displayEntries.push(requestGroup);
      }

      requestGroups.get(referenceId).items.push(item);
    };

    ineligibleDisplayItems.forEach((entry) => {
      if (entry.type === "single") {
        const referenceId = getTrackableRequestReferenceId(entry.item);

        if (referenceId) {
          addRequestItem(entry.item, referenceId);
          return;
        }

        displayEntries.push(entry);
        return;
      }

      const children = entry.children || [];
      const trackableChildren = children
        .map((child) => ({
          child,
          referenceId: getTrackableRequestReferenceId(child),
        }))
        .filter(({ referenceId }) => Boolean(referenceId));

      if (children.length > 0 && trackableChildren.length === children.length) {
        trackableChildren.forEach(({ child, referenceId }) => {
          addRequestItem(child, referenceId);
        });
        return;
      }

      displayEntries.push(entry);
    });

    return displayEntries;
  }, [getTrackableRequestReferenceId, ineligibleDisplayItems]);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = stripVersionPrefix(location.pathname);
  const exchangeStep = params.get("exchangeStep");
  const prevExchangeStepRef = useRef(exchangeStep);
  const isV2ExchangeFlow =
    currentPath === "/products" &&
    ["type", "same", "different"].includes(exchangeStep || "");

  const navigateProductsStep = useCallback(
    (step = null) => {
      const nextParams = new URLSearchParams(params.toString());

      if (step) {
        nextParams.set("exchangeStep", step);
      } else {
        nextParams.delete("exchangeStep");
      }

      const nextQuery = nextParams.toString();
      const nextUrl = `${versionedPath("/products", location.pathname)}${
        nextQuery ? `?${nextQuery}` : ""
      }`;

      navigate(nextUrl);
    },
    [location.pathname, navigate, params],
  );

  const navigateToOrderSummary = useCallback(() => {
    let url = versionedPath("/orders", location.pathname);
    if (encipherencode) {
      url += `?encipherencode=${encipherencode}`;
    }
    navigate(url);
  }, [encipherencode, location.pathname, navigate]);

  const navigateReturnCart = useCallback(
    (
      productsForCart = updatedReturnedProducts,
      { warrantyClaim = false } = {},
    ) => {
      if (!productsForCart || productsForCart.length === 0) {
        return;
      }

      saveReturnCartContext({
        selectedOrder,
        returnedProducts: productsForCart,
        shipmentMethod,
        refundSource,
        refundJSON,
        addressDetails,
      });

      const returnCartParams = new URLSearchParams();
      if (encipherencode) {
        returnCartParams.set("encipherencode", encipherencode);
      }
      if (isWarrantyClaimFlow || warrantyClaim) {
        returnCartParams.set("claim_warrenty", "true");
      }
      const query = returnCartParams.toString();
      const url = `${versionedPath("/return-cart", location.pathname)}${query ? `?${query}` : ""}`;
      navigate(url);
    },
    [
      addressDetails,
      encipherencode,
      isWarrantyClaimFlow,
      location.pathname,
      navigate,
      refundJSON,
      refundSource,
      selectedOrder,
      shipmentMethod,
      updatedReturnedProducts,
    ],
  );

  const openTrackingRequest = useCallback(
    (referenceId) => {
      if (!hasValidTrackingReference(referenceId)) {
        return;
      }

      const trackingParams = new URLSearchParams();
      if (encipherencode) {
        trackingParams.set("encipherencode", encipherencode);
      }
      trackingParams.set("rid", referenceId);
      trackingParams.set("back_to", "products");
      dispatch(updateReturnOrder(""));

      navigate(
        `${versionedPath("/tracking", location.pathname)}?${trackingParams.toString()}`,
      );
    },
    [dispatch, encipherencode, location.pathname, navigate],
  );

  const hasPendingEligibleItems = useCallback(
    (order, cartProducts = updatedReturnedProducts) => {
      const items = order?.order_items || [];
      if (items.length === 0) {
        return false;
      }

      const remainingQtyByLineItem = new Map(
        (cartProducts || []).map((item) => [
          item.line_item_id,
          Number(item.remaining_qty),
        ]),
      );

      return items.some((item) => {
        if (!item?.visible) {
          return false;
        }

        if (isWarrantyClaimFlow) {
          const remainingWarrantyQty = remainingQtyByLineItem.has(
            item.line_item_id,
          )
            ? Number(remainingQtyByLineItem.get(item.line_item_id))
            : Number(
                item.warranty_claim_remaining_qty ?? item.remaining_qty ?? 0,
              );
          return (
            item.warranty_claim_eligible === true &&
            remainingWarrantyQty > 0
          );
        }

        if (
          (!item?.eligible && !isReplacementAvailable(item)) ||
          !item?.visible
        ) {
          return false;
        }

        const baseQty =
          item.remaining_qty !== undefined && item.remaining_qty !== null
            ? Number(item.remaining_qty)
            : Number(item.quantity || 0);
        const effectiveQty = remainingQtyByLineItem.has(item.line_item_id)
          ? Number(remainingQtyByLineItem.get(item.line_item_id))
          : baseQty;

        return effectiveQty > 0;
      });
    },
    [isReplacementAvailable, isWarrantyClaimFlow, updatedReturnedProducts],
  );

  const handlePostSelectionNavigation = useCallback(
    (productsForCart = updatedReturnedProducts) => {
      const hasPending = hasPendingEligibleItems(
        selectedOrder,
        productsForCart,
      );

      setActiveLineItemId(null);
      setActiveLineAction(null);
      setCanContinue(false);
      setReasonValidationAttempted(false);
      setSelectedLineCount(0);
      reasonSubmitRef.current = null;
      sameExchangeSubmitRef.current = null;

      dispatch(addProductType(null));
      setExchangeSelectedVariant(null);
      setExchangeRequiredProduct(null);
      setExchangeOpenFilter(false);

      if (hasPending) {
        navigateProductsStep(null);
        return;
      }

      navigateReturnCart(productsForCart);
    },
    [
      dispatch,
      hasPendingEligibleItems,
      navigateProductsStep,
      navigateReturnCart,
      selectedOrder,
      updatedReturnedProducts,
    ],
  );

  const collapseActiveLineItem = useCallback(() => {
    setActiveLineItemId(null);
    setActiveLineAction(null);
    setCanContinue(false);
    setReasonValidationAttempted(false);
    setSelectedLineCount(0);
    reasonSubmitRef.current = null;
  }, []);

  function updateProductsList(order) {
    const updatedOrder = {
      ...order,
      order_items: [...(order?.order_items || [])],
    };
    const cartProducts = updatedReturnedProducts || [];

    if (cartProducts.length > 0) {
      const remainingQtyByLineItem = new Map(
        cartProducts.map((item) => [item.line_item_id, item.remaining_qty]),
      );

      updatedOrder.order_items = updatedOrder.order_items.map((item) => {
        if (!remainingQtyByLineItem.has(item.line_item_id)) {
          return item;
        }

        return {
          ...item,
          remaining_qty: remainingQtyByLineItem.get(item.line_item_id),
        };
      });
    }

    updatedOrder.order_items = updatedOrder.order_items.sort(
      compareOrderItemsForDisplay,
    );

    setSelectedOrder(updatedOrder);
  }

  useEffect(() => {
    if (
      !requiresDeepLinkOrderBootstrap ||
      !merchantConfig?.merchant_id ||
      String(selectedOrder?.order_id) === String(deepLinkOrderId)
    ) {
      return;
    }

    let isCurrentRequest = true;
    setProductsLoading(true);

    ordersList(0, undefined, deepLinkOrderId)
      .then((response) => {
        if (!isCurrentRequest) {
          return;
        }

        const order = (response.data?.orders || []).find(
          (candidate) =>
            String(candidate.order_id) === String(deepLinkOrderId),
        );

        if (!order || order.eligible === false) {
          throw new Error("The requested order is not eligible.");
        }

        return selectedOrderAPI(order);
      })
      .then((response) => {
        if (!isCurrentRequest) {
          return;
        }

        const order = response.data?.order;
        if (!order?.order_id || !Array.isArray(order.order_items)) {
          throw new Error("We could not load the requested order details.");
        }

        dispatch(updateSelectedOrder(order));
        dispatch(updateUUID(response.data?.unique_id));
        dispatch(addAddress(getOrderPickupAddress(order)));
        dispatch(updateServiceability(true));
        dispatch(addShipmentMethod("merchant_handles_return_shipment"));
        // Entering another order must not reuse the previous order's cart or drafts.
        clearReturnCartContext();
        dispatch(addReturnedProducts([]));
        dispatch(addUploadMediaIds([]));
        dispatch(addRefundSource(null));
        dispatch(addRefundJSON({}));
        resetReasonDraft();
        setSelectedOrder(order);
        setApiErrorLoading(false);
        setProductsLoading(false);
      })
      .catch(() => {
        if (!isCurrentRequest) {
          return;
        }

        setApiErrorLoading(true);
        setProductsLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    deepLinkOrderId,
    dispatch,
    merchantConfig?.merchant_id,
    requiresDeepLinkOrderBootstrap,
    resetReasonDraft,
    selectedOrder?.order_id,
  ]);

  useEffect(() => {
    setUpdatedReturnedProducts(returnedProducts);
  }, [returnedProducts]);

  useEffect(() => {
    updateProductsList(defaultSelectedOrder);
  }, [updatedReturnedProducts]);

  useEffect(() => {
    if (
      merchantConfig &&
      merchantConfig.merchant_id !== null &&
      selectedOrder &&
      selectedOrder.order_id &&
      selectedOrder.order_id !== null &&
      !requiresDeepLinkOrderBootstrap
    ) {
      selectedOrderAPI(selectedOrder)
        .then((resp) => {
          dispatch(updateSelectedOrder(resp.data.order));
          dispatch(updateUUID(resp.data.unique_id));
          if (currentPath === "/products") {
            updateProductsList(resp.data.order);
            // setSelectedOrder(resp.data.order);
          }
          setProductsLoading(false);
        })
        .catch((err) => {
          if (userPhone === "" && userEmail === "") {
            let url = "/";
            if (encipherencode) {
              url += `?encipherencode=${encipherencode}`;
            }
            navigate(url);
          } else {
            setApiErrorLoading(true);
          }
        });
	      dispatch(addProductid(null));
	      setProductid(null);
	      const storedReturnCartContext = getReturnCartContext();
	      const hasStoredReturnCart =
	        Array.isArray(storedReturnCartContext?.returnedProducts) &&
	        storedReturnCartContext.returnedProducts.length > 0;
	      if (
	        (!returnedProducts || returnedProducts.length === 0) &&
	        !hasStoredReturnCart
	      ) {
	        dispatch(addRefundSource(null));
	        dispatch(addRefundJSON({}));
	      }
    }
  }, []);

  useEffect(() => {
    if (!isV2ExchangeFlow) {
      setExchangeSelectedVariant(null);
      setExchangeRequiredProduct(null);
      setExchangeOpenFilter(false);
      prevExchangeStepRef.current = exchangeStep;
      prevExchangeLineItemRef.current =
        selectedProductState?.line_item_id || null;
      return;
    }

    if (!selectedProductState?.line_item_id || !selectedReasonState) {
      navigateProductsStep(null);
      prevExchangeStepRef.current = exchangeStep;
      prevExchangeLineItemRef.current =
        selectedProductState?.line_item_id || null;
      return;
    }

    const currentLineItemId = selectedProductState?.line_item_id || null;
    const enteredTypeStep =
      exchangeStep === "type" && prevExchangeStepRef.current !== "type";
    const changedLineItemOnTypeStep =
      exchangeStep === "type" &&
      prevExchangeLineItemRef.current !== currentLineItemId;

    if (enteredTypeStep || changedLineItemOnTypeStep) {
      setExchangeSelectedVariant(null);
      setExchangeRequiredProduct(null);
      setExchangeOpenFilter(false);
    }

    prevExchangeStepRef.current = exchangeStep;
    prevExchangeLineItemRef.current = currentLineItemId;
  }, [
    exchangeStep,
    isV2ExchangeFlow,
    navigateProductsStep,
    selectedProductState?.line_item_id,
    selectedReasonState,
  ]);

  const styles = {
    infoMessage: {
      color: "var(--rms-v2-text-muted)",
      fontSize: "14px",
      padding: "1rem",
    },
    button: {
      padding: rmsConfig.button_version === "v2" ? "10px 6px" : "2px 1rem",
      borderRadius: merchantConfig.use_rounded_edge_buttons ? "10px" : "0px",
      fontWeight: "700",
      fontSize: "16px",
    },
    incentiviseMessage: {
      color: rmsConfig.button_promotional_text_color
        ? rmsConfig.button_promotional_text_color
        : "var(--rms-v2-success)",
    },
  };

  const openReasonFlow = (returnType, flowProduct, options = {}) => {
    if (!flowProduct) {
      return;
    }

    const selectionReturnType =
      returnType === "warranty" ? "warranty_claim" : returnType;

    handleOnSelect(selectionReturnType, flowProduct, options);
    setActiveLineAction(
      options.isReplacement
        ? "replacement"
        : returnType === "exchange"
          ? "exchange"
          : returnType === "warranty"
            ? "warranty"
            : "return",
    );
    setActiveLineItemId(flowProduct.line_item_id);
    setCanContinue(false);
    setReasonValidationAttempted(false);
    setReasonSubmitting(false);
    setIncentiviseExchangeModalOpen(false);
    setPendingIncentiviseSelection(null);
  };

  const isDeepLinkActionEligible = useCallback(
    (product, returnType) => {
      if (!product?.visible || getRemainingQuantity(product) <= 0) {
        return false;
      }

      if (returnType === "warranty") {
        return product.warranty_claim_eligible === true;
      }

      if (!product.is_delivered) {
        return false;
      }

      if (returnType === "return") {
        return Boolean(
          rmsConfig.supports_refund &&
            !product.return_date_expired &&
            product.refund_eligible,
        );
      }

      if (returnType === "replacement") {
        return Boolean(
          !product.delivery_date_expired && isReplacementAvailable(product),
        );
      }

      return Boolean(
        customerCanExchange &&
          !product.exchange_date_expired &&
          product.exchange_eligible,
      );
    },
    [
      customerCanExchange,
      getRemainingQuantity,
      isReplacementAvailable,
      rmsConfig.supports_refund,
    ],
  );

  useEffect(() => {
    if (
      hasAppliedReturnFlowDeepLinkRef.current ||
      productsLoading ||
      apiErrorLoading ||
      requiresDeepLinkOrderBootstrap ||
      !returnFlowDeepLink
    ) {
      return;
    }

    hasAppliedReturnFlowDeepLinkRef.current = true;
    const deepLinkedProduct = (selectedOrder?.order_items || []).find(
      (product) =>
        String(product.line_item_id) === String(returnFlowDeepLink.lineItemId) ||
        String(product.id) === String(returnFlowDeepLink.lineItemId),
    );

    if (
      !deepLinkedProduct ||
      !isDeepLinkActionEligible(
        deepLinkedProduct,
        returnFlowDeepLink.returnType,
      )
    ) {
      toast.error(
        "The requested item is not eligible for this return type.",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
      return;
    }

    openReasonFlow(
      returnFlowDeepLink.returnType === "replacement"
        ? "exchange"
        : returnFlowDeepLink.returnType,
      deepLinkedProduct,
      {
        isReplacement: returnFlowDeepLink.returnType === "replacement",
      },
    );
  }, [
    apiErrorLoading,
    requiresDeepLinkOrderBootstrap,
    isDeepLinkActionEligible,
    openReasonFlow,
    productsLoading,
    returnFlowDeepLink,
    selectedOrder?.order_items,
  ]);

  // Helper function to render buttons
  const BUTTON_CONTAINER_GAP = 10;
  const MIN_BUTTON_WIDTH = 140;

  const getProductRequestWindows = (product) =>
    resolveRequestWindows({
      subject: product,
      rmsConfig,
      deliveredAt: product?.delivered_at || selectedOrder.delivered_at,
      forceExchange: selectedOrder.force_exchange,
    });

  const ExpiryInfo = ({
    showExchange,
    showReturn,
    requestWindows,
    className = "",
    style,
  }) => {
    const exchangeLabel = rmsConfig.only_replacement
      ? "Replacement"
      : "Exchange";
    const showCombined =
      showExchange && showReturn && requestWindows?.sameDate;

    if (
      (!showReturn || !requestWindows?.returnDate) &&
      (!showExchange || !requestWindows?.exchangeDate)
    ) {
      return null;
    }

    return (
      <div className={`expiry-info ${className}`.trim()} style={style}>
        {showCombined ? (
          <div className="expiry-info-item">
            <AccessTimeIcon fontSize="small" />
            <span>
              Return/{exchangeLabel} before{" "}
              {formatRequestWindow(requestWindows.returnDate, "MMM do ''yy")}
            </span>
          </div>
        ) : (
          <>
            {showExchange && requestWindows.exchangeDate && (
              <div className="expiry-info-item">
                <AccessTimeIcon fontSize="small" />
                <span>
                  {exchangeLabel} before{" "}
                  {formatRequestWindow(
                    requestWindows.exchangeDate,
                    "MMM do ''yy",
                  )}
                </span>
              </div>
            )}
            {showReturn && requestWindows.returnDate && (
              <div className="expiry-info-item">
                <AccessTimeIcon fontSize="small" />
                <span>
                  Return before{" "}
                  {formatRequestWindow(
                    requestWindows.returnDate,
                    "MMM do ''yy",
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderButton = (
    label,
    onClick,
    disabled,
    icon,
    style = {},
    chargesStatement,
    buttonType,
    isActive = false,
  ) => {
    const themeColor = merchantConfig.primary_theme_color || "#1d8dcc";
    const resolvedType =
      buttonType ||
      (label === "Exchange" || label === "Replacement" ? "exchange" : "return");
    const isContained =
      resolvedType === "exchange" || resolvedType === "replace";
    const activeStyles = isActive
      ? isContained
        ? {
            backgroundColor: themeColor,
            borderColor: themeColor,
            color: "var(--rms-v2-surface)",
          }
        : {
            borderColor: themeColor,
            color: themeColor,
            backgroundColor: `${themeColor}1A`,
          }
      : {};

    const buttonClassNames = ["rms-btns"];
    if (label === "Exchange" || label === "Replacement") {
      buttonClassNames.push("exchange-button");
    }
    if (isActive) {
      buttonClassNames.push("active");
    }

    const resolvedVariant = isContained ? "contained" : "outlined";

    return (
      <Button
        className={buttonClassNames.join(" ")}
        variant={resolvedVariant}
        color="primary"
        onClick={onClick}
        disabled={disabled}
        startIcon={icon}
        style={{ ...styles.button, ...style, ...activeStyles }}
      >
        {label}
        <span className="charges-statement">{chargesStatement}</span>
      </Button>
    );
  };

  const renderTrackRequestButton = (referenceId) => {
    if (!hasValidTrackingReference(referenceId)) {
      return null;
    }

    return (
      <div className="button-container track-request-container">
        {renderButton(
          "Track Request",
          () => openTrackingRequest(referenceId),
          false,
          null,
          {
            width: "100%",
            maxWidth: "100%",
            flexBasis: "100%",
            background: "var(--rms-v2-surface)",
            border: `1px solid ${merchantConfig.primary_theme_color}`,
            color: merchantConfig.primary_theme_color,
          },
          null,
          "return",
        )}
      </div>
    );
  };

  const RefundButtons = ({ product, onActionClick, selectedAction }) => {
    // Helper function to render restriction messages
    const renderRestrictionMessage = (message) => (
      <p className="info-message">{message}</p>
    );

    const requestWindows = getProductRequestWindows(product);

    const openExchangeFlow = (isReplacement = false) => {
      openReasonFlow("exchange", product, { isReplacement });
    };

    const openReturnFlow = () => {
      if (
        rmsConfig.exchange_nudge_on_return_click &&
        customerCanExchange &&
        !product.exchange_date_expired &&
        product.exchange_eligible
      ) {
        resetReasonDraft();
        setPendingIncentiviseSelection({
          product,
          returnType: "return",
          isReplacement: false,
        });
        setIncentiviseExchangeModalOpen(true);
      } else {
        openReasonFlow("return", product, { isReplacement: false });
      }
    };

    const handleExchangeClick = (isReplacement = false) => {
      if (onActionClick) {
        onActionClick("exchange", { isReplacement });
        return;
      }
      openExchangeFlow(isReplacement);
    };

    const handleReturnClick = () => {
      if (onActionClick) {
        onActionClick("return", { isReplacement: false });
        return;
      }
      openReturnFlow();
    };

    const isExchangeActive =
      selectedAction?.type === "exchange" && !selectedAction?.isReplacement;
    const isReplaceActive =
      selectedAction?.type === "exchange" && selectedAction?.isReplacement;
    const isReturnActive = selectedAction?.type === "return";
    const trackingReferenceId = getLineItemReferenceId(product);

    // Conditions
    if (!product.is_delivered || product.is_delivered == null) {
      return <p className="info-message">The item is yet to be delivered</p>;
    }

    if (product.is_delivered && product.delivery_date_expired) {
      return (
        <p className="info-message">
          {rmsConfig.only_replacement
            ? "Replacement"
            : rmsConfig.supports_exchange && rmsConfig.supports_refund
              ? "Return/Exchange"
              : rmsConfig.supports_exchange
                ? "Exchange"
                : "Return"}{" "}
          period is expired
        </p>
      );
    }

    if (
      rmsConfig.customer_flow_preference === "reason_first" &&
      !isWarrantyClaimFlow
    ) {
      return (
        <div className="button-container single-action">
          {renderButton(
            "Choose reason",
            () => {
              if (onActionClick) {
                onActionClick("reason_first");
              } else {
                openReasonFlow("reason_first", product);
              }
            },
            !(product.refund_eligible || product.exchange_eligible || isReplacementAvailable(product)),
            null,
            { width: "100%", maxWidth: "100%", flexBasis: "100%" },
            null,
            "return",
          )}
        </div>
      );
    }

    const getButtonSizeStyles = (buttons) => {
      const visibleButtons = buttons.filter(Boolean);
      const buttonCount = visibleButtons.length;

      if (buttonCount === 0) {
        return {};
      }

      const totalGap = Math.max(buttonCount - 1, 0) * BUTTON_CONTAINER_GAP;
      const isButtonVersionV1 = rmsConfig.button_version === "v1";
      const hasExchangeAndReturn =
        visibleButtons.includes("exchange") &&
        visibleButtons.includes("return");

      const baseShares = visibleButtons.reduce((acc, button) => {
        acc[button] = buttonCount > 1 ? 45 : 100;
        return acc;
      }, {});

      if (buttonCount === 2 && !isButtonVersionV1 && hasExchangeAndReturn) {
        baseShares.exchange = 80;
        baseShares.return = 20;
      }

      const totalShare = visibleButtons.reduce(
        (sum, button) => sum + (baseShares[button] || 0),
        0,
      );

      return visibleButtons.reduce((acc, button) => {
        if (buttonCount === 1) {
          acc[button] = {
            width: "100%",
            maxWidth: "100%",
            flexBasis: "100%",
            flexGrow: 0,
            flexShrink: 0,
          };
          return acc;
        }

        const normalizedShare =
          totalShare > 0 ? baseShares[button] / totalShare : 1 / buttonCount;
        const shareValue = Number(normalizedShare.toFixed(4));
        const widthValue =
          totalGap > 0
            ? `calc((100% - ${totalGap}px) * ${shareValue})`
            : `${shareValue * 100}%`;

        const minWidthValue =
          buttonCount > 1
            ? `min(${MIN_BUTTON_WIDTH}px, ${widthValue})`
            : `${MIN_BUTTON_WIDTH}px`;

        acc[button] = {
          width: widthValue,
          maxWidth: widthValue,
          flexBasis: widthValue,
          flexGrow: 0,
          flexShrink: 0,
          minWidth: minWidthValue,
        };

        return acc;
      }, {});
    };

    if (
      rmsConfig.restrict_multi_type_request_creation &&
      returnedProducts.length > 0 &&
      (product.refund_eligible ||
        product.exchange_eligible ||
        isReplacementAvailable(product)) &&
      product.remaining_qty > 0
    ) {
      const showExchangeButton =
        customerCanExchange &&
        !product.exchange_date_expired &&
        returnedProducts.some((a) => a.return_type === "exchange") &&
        product.exchange_eligible;

      const showReturnButton =
        rmsConfig.supports_refund &&
        !product.return_date_expired &&
        returnedProducts.some((a) => a.return_type === "refund") &&
        product.refund_eligible;

      const showReplaceButton =
        isReplacementAvailable(product) &&
        returnedProducts.some(
          (item) => item.return_type === "replacement",
        );

      const visibleButtons = [
        showExchangeButton ? "exchange" : null,
        showReplaceButton ? "replace" : null,
        showReturnButton ? "return" : null,
      ];

      const buttonSizeStyles = getButtonSizeStyles(visibleButtons);
      const buttonContainerClassName = `button-container ${
        visibleButtons.filter(Boolean).length === 1 ? "single-action" : ""
      }`;

      return (
        <div>
          <div className={buttonContainerClassName}>
            {showExchangeButton &&
              renderButton(
                "Exchange",
                () => handleExchangeClick(false),
                !product.exchange_eligible,
                <RepeatIcon />,
                {
                  minWidth: `${MIN_BUTTON_WIDTH}px`,
                  ...(buttonSizeStyles.exchange || {}),
                },
                null,
                "exchange",
                isExchangeActive,
              )}
            {showReplaceButton &&
              renderButton(
                "Replacement",
                () => handleExchangeClick(true),
                false,
                <RepeatIcon />,
                {
                  minWidth: `${MIN_BUTTON_WIDTH}px`,
                  ...(buttonSizeStyles.replace || {}),
                },
                null,
                "replace",
                isReplaceActive,
              )}
            {showReturnButton &&
              renderButton(
                rmsConfig.button_version !== "v2" ? "Return" : "",
                () => handleReturnClick(),
                !product.refund_eligible,
                rmsConfig.button_version === "v3" ||
                  rmsConfig.button_version === "v4" ? (
                  ""
                ) : (
                  <ReplayIcon />
                ),
                {
                  minWidth: `${MIN_BUTTON_WIDTH}px`,
                  ...(buttonSizeStyles.return || {}),
                  display: "flex",
                  flexDirection:
                    rmsConfig.button_version === "v4" ? "column" : "row",
                  background: "#f9fafc",
                  border:
                    merchantConfig.merchant_id === 977
                      ? "1px solid #E3E6EE"
                      : `1px solid ${merchantConfig.primary_theme_color}`,
                },
                rmsConfig.button_version === "v4" &&
                  rmsConfig.base_refund_charge &&
                  rmsConfig.base_refund_charge !== null &&
                  `Extra ${rmsConfig.base_refund_charge}*`,
                "return",
                isReturnActive,
              )}
            {rmsConfig.button_promotional_text &&
              rmsConfig.button_promotional_text !== "" && (
                <p
                  className="incentivise-message"
                  style={styles.incentiviseMessage}
                >
                  {rmsConfig.button_promotional_text}
                </p>
              )}
          </div>

          <ExpiryInfo
            showExchange={showExchangeButton}
            showReturn={showReturnButton}
            requestWindows={requestWindows}
            style={{
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "10px"
                : "0px",
            }}
          />

          {!product.exchange_eligible &&
            renderRestrictionMessage(
              product.exchange_restrict_msg ||
                `This item is not eligible for ${
                  rmsConfig.only_replacement ? "replace" : "exchange"
                }`,
            )}

          {!product.refund_eligible &&
            product.refund_restrict_msg &&
            renderRestrictionMessage(product.refund_restrict_msg)}
        </div>
      );
    }

    if (
      (product.eligible || isReplacementAvailable(product)) &&
      (product.refund_eligible ||
        product.exchange_eligible ||
        isReplacementAvailable(product)) &&
      product.remaining_qty > 0
    ) {
      const showExchangeButton =
        customerCanExchange &&
        !product.exchange_date_expired &&
        product.exchange_eligible;

      const showReturnButton =
        rmsConfig.supports_refund &&
        !product.return_date_expired &&
        product.refund_eligible;

      const showReplaceButton =
        !isWarrantyClaimFlow && isReplacementAvailable(product);

      const visibleButtons = [
        showExchangeButton ? "exchange" : null,
        showReplaceButton ? "replace" : null,
        showReturnButton ? "return" : null,
      ];
      const visibleButtonCount = visibleButtons.filter(Boolean).length;

      if (visibleButtonCount === 0) {
        return renderRestrictionMessage(
          product.exchange_restrict_msg ||
            product.refund_restrict_msg ||
            "This item is not eligible for the available request actions",
        );
      }

      const buttonSizeStyles = getButtonSizeStyles(visibleButtons);
      const buttonContainerClassName = `button-container ${
        visibleButtonCount === 1 ? "single-action" : ""
      }`;

      return (
        <div>
          <div className={buttonContainerClassName}>
            {showExchangeButton &&
              renderButton(
                "Exchange",
                () => handleExchangeClick(false),
                !product.exchange_eligible,
                <RepeatIcon />,
                {
                  minWidth: `${MIN_BUTTON_WIDTH}px`,
                  ...(buttonSizeStyles.exchange || {}),
                },
                null,
                "exchange",
                isExchangeActive,
              )}
            {showReplaceButton &&
              renderButton(
                "Replacement",
                () => handleExchangeClick(true),
                false,
                <RepeatIcon />,
                {
                  minWidth: `${MIN_BUTTON_WIDTH}px`,
                  ...(buttonSizeStyles.replace || {}),
                },
                null,
                "replace",
                isReplaceActive,
              )}
            {showReturnButton &&
              renderButton(
                rmsConfig.button_version !== "v2" ? "Return" : "",
                () => handleReturnClick(),
                !product.refund_eligible,
                rmsConfig.button_version === "v3" ||
                  rmsConfig.button_version === "v4" ? (
                  ""
                ) : (
                  <ReplayIcon />
                ),
                {
                  minWidth: `${MIN_BUTTON_WIDTH}px`,
                  ...(buttonSizeStyles.return || {}),
                  display: "flex",
                  flexDirection:
                    rmsConfig.button_version === "v4" ? "column" : "row",
                  background: "#f9fafc",
                  border:
                    merchantConfig.merchant_id === 977
                      ? "1px solid #E3E6EE"
                      : `1px solid ${merchantConfig.primary_theme_color}`,
                },
                rmsConfig.button_version === "v4" &&
                  rmsConfig.base_refund_charge &&
                  rmsConfig.base_refund_charge !== null &&
                  `Extra ${rmsConfig.base_refund_charge}*`,
                "return",
                isReturnActive,
              )}
          </div>

          {rmsConfig.button_promotional_text &&
            rmsConfig.button_promotional_text !== "" && (
              <p
                className="incentivise-message"
                style={styles.incentiviseMessage}
              >
                {rmsConfig.button_promotional_text}
              </p>
            )}

          <ExpiryInfo
            showExchange={showExchangeButton}
            showReturn={showReturnButton}
            requestWindows={requestWindows}
            style={{
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "10px"
                : "0px",
              background:
                merchantConfig.merchant_id === "977"
                  ? "#E6EEFF"
                  : `color-mix(in srgb, ${merchantConfig.primary_theme_color} 15%, white)`,
            }}
          />

          {!product.exchange_eligible &&
            product.exchange_restrict_msg &&
            renderRestrictionMessage(product.exchange_restrict_msg)}

          {!product.refund_eligible &&
            product.refund_restrict_msg &&
            renderRestrictionMessage(product.refund_restrict_msg)}

          {product.errors?.length > 0 &&
            product.errors.map((err, idx) => (
              <p className="info-message" key={idx}>
                {err.error_code_message}
              </p>
            ))}
        </div>
      );
    }

    if (product.remaining_qty > 0) {
      return (
        <p className="info-message">
          This item is not eligible for{" "}
          {rmsConfig.only_replacement ? "replacement" : "return or exchange"}
        </p>
      );
    }

    return (
      <div>
        <p className="info-message">
          You've already placed a{" "}
          {rmsConfig.only_replacement ? "replacement" : "return/exchange"} for
          this item.
          {!trackingReferenceId &&
            " Please reach out to us by clicking on the Need Help button at the bottom of the page for further assistance."}
        </p>
        {renderTrackRequestButton(trackingReferenceId)}
      </div>
    );
  };

  function handleOnSelect(return_type, product, options = {}) {
    const { isReplacement = false } = options;
    const selectedQuantity =
      activeLineItemQuantityRef.current[product.line_item_id] ||
      getDisplayQuantity(product) ||
      product.remaining_qty ||
      product.quantity ||
      1;
    const selectedProductPayload = {
      ...product,
      quantity: selectedQuantity,
      remaining_qty: selectedQuantity,
    };

    dispatch(addSelectedReason(null));
    dispatch(addSelectedSubReason(null));
    dispatch(setImageUploads([]));
    dispatch(addPreviews([]));
    dispatch(addUploads([]));
    dispatch(addProductType(null));
    dispatch(addProceedBool(false));
    dispatch(setReplacementMode(isReplacement));

    if (isReplacement) {
      dispatch(addProductType("same"));
    }

    if (product.product_group_id && product.product_group_id !== null) {
      let groupProducts = selectedOrder.order_items.filter(
        (newProduct) =>
          newProduct.product_group_id === product.product_group_id,
      );
      if (product.is_parent) {
        dispatch(addSelectedProduct(selectedProductPayload));
        dispatch(addSelectedGroupProducts(groupProducts));
        dispatch(addProductid(product.line_item_id + return_type));
        setProductid(product.line_item_id + return_type);
        setSelectedProduct(selectedProductPayload);
      } else {
        const parentProduct = groupProducts.filter(
          (groupProduct) => groupProduct.is_parent === true,
        );

        if (parentProduct && parentProduct.length > 0) {
          dispatch(addSelectedProduct(parentProduct[0]));
          setSelectedProduct(parentProduct[0]);
          dispatch(addSelectedGroupProducts(groupProducts));
          dispatch(addProductid(parentProduct[0].line_item_id + return_type));
          setProductid(parentProduct[0].line_item_id + return_type);
        }
      }
    } else {
      dispatch(addSelectedProduct(selectedProductPayload));
      setSelectedProduct(selectedProductPayload);
      dispatch(addSelectedGroupProducts([]));
      dispatch(addProductid(product.line_item_id + return_type));
      setProductid(product.line_item_id + return_type);
    }
  }

  const renderProductPrice = (item) => {
    if (!item || item.product_price == null) {
      return null;
    }

    if (item.total_item_discount > 0) {
      return (
        <span className="line-item-price-stack">
          <span className="line-item-current-price">
            ₹ {item.discounted_price}
          </span>
          <span className="line-item-compare-price">
            ₹{item.original_product_price}
          </span>
        </span>
      );
    }

    return `₹ ${item.product_price}`;
  };

  const getIneligibleMessages = (product) => {
    const errorMessages = dedupeErrors(product?.errors || [])
      .map((error) => error?.error_code_message)
      .filter(Boolean);

    if (errorMessages.length > 0) {
      return errorMessages;
    }

    if (
      isWarrantyClaimFlow &&
      product?.warranty_claim_eligible !== true
    ) {
      return ["This item is not eligible for a warranty claim."];
    }

    if (product?.refund_restrict_msg) {
      return [product.refund_restrict_msg];
    }

    if (product?.exchange_restrict_msg) {
      return [product.exchange_restrict_msg];
    }

    if (product?.remaining_qty <= 0) {
      return [
        `You've already placed a ${
          rmsConfig.only_replacement ? "replacement" : "return/exchange"
        } for this item.`,
      ];
    }

    return [
      `This item is not eligible for ${
        rmsConfig.only_replacement ? "replacement" : "return or exchange"
      }.`,
    ];
  };

  const getDisplayQuantity = (item) =>
    getRemainingQuantity(item);

  const getLineItemQuantity = (product) =>
    activeLineItemQuantityRef.current[product.line_item_id] ||
    getDisplayQuantity(product) ||
    1;

  const setLineItemQuantity = (product, quantity) => {
    const nextQuantity = Number(quantity);
    activeLineItemQuantityRef.current = {
      ...activeLineItemQuantityRef.current,
      [product.line_item_id]: nextQuantity,
    };
    const selectedProductPayload = {
      ...product,
      quantity: nextQuantity,
      remaining_qty: nextQuantity,
    };
    dispatch(addSelectedProduct(selectedProductPayload));
    setSelectedProduct(selectedProductPayload);
  };

  const canEditLineItemQuantity = (product) => {
    const maxQuantity = Math.max(Number(getDisplayQuantity(product)) || 1, 1);
    return rmsConfig.supports_return_partial_qtty && maxQuantity > 1;
  };

  const updateLineItemQuantity = (product, nextQuantity) => {
    const maxQuantity = Math.max(Number(getDisplayQuantity(product)) || 1, 1);
    const boundedQuantity = Math.min(
      Math.max(Number(nextQuantity), 1),
      maxQuantity,
    );
    setLineItemQuantity(product, boundedQuantity);
    setSelectedLineCount(boundedQuantity);
  };

  const renderLineItemQuantityEditor = (product) => {
    if (!canEditLineItemQuantity(product)) {
      return null;
    }

    const quantity = getLineItemQuantity(product);
    const maxQuantity = Math.max(Number(getDisplayQuantity(product)) || 1, 1);

    return (
      <div className="line-item-quantity-editor">
        <p className="field-label">Quantity</p>
        <div className="line-item-quantity-editor-row">
          <div className="line-item-quantity-stepper">
            <button
              type="button"
              aria-label="Decrease quantity"
              disabled={quantity <= 1}
              onClick={(event) => {
                event.stopPropagation();
                updateLineItemQuantity(product, quantity - 1);
              }}
            >
              -
            </button>
            <span>{quantity}</span>
            <button
              type="button"
              aria-label="Increase quantity"
              disabled={quantity >= maxQuantity}
              onClick={(event) => {
                event.stopPropagation();
                updateLineItemQuantity(product, quantity + 1);
              }}
            >
              +
            </button>
          </div>
          <span className="line-item-quantity-limit">
            Max {maxQuantity} units per order
          </span>
        </div>
      </div>
    );
  };

  const getLineItemExpiryInfoProps = (product) => {
    const requestWindows = getProductRequestWindows(product);
    const showExchange =
      requestWindows.exchangeDate &&
      customerCanExchange &&
      !product.exchange_date_expired &&
      product.exchange_eligible;
    const showReturn =
      requestWindows.returnDate &&
      rmsConfig.supports_refund &&
      !product.return_date_expired &&
      product.refund_eligible;

    return {
      showExchange,
      showReturn,
      requestWindows,
      className: "expiry-info-card",
    };
  };

  const ActionSelector = ({ product, activeAction }) => {
    const apiWarrantyEligible = product.warranty_claim_eligible === true;
    const canWarranty = isWarrantyClaimFlow && apiWarrantyEligible;
    const warrantyOnly = isWarrantyClaimFlow;
    const canExchange =
      !warrantyOnly &&
      customerCanExchange &&
      !product.exchange_date_expired &&
      product.exchange_eligible;
    const canReturn =
      !warrantyOnly &&
      rmsConfig.supports_refund &&
      !product.return_date_expired &&
      product.refund_eligible;
    const canReplace = !warrantyOnly && isReplacementAvailable(product);

    if (
      (!product.is_delivered || product.is_delivered == null) &&
      !canWarranty
    ) {
      return <p className="info-message">The item is yet to be delivered</p>;
    }

    if (
      product.is_delivered &&
      product.delivery_date_expired &&
      !canWarranty
    ) {
      return <p className="info-message">Return/Exchange period is expired</p>;
    }

    if (!canExchange && !canReplace && !canReturn && !canWarranty) {
      return (
        <p className="info-message">
          {isWarrantyClaimFlow
            ? "This item is not eligible for a warranty claim."
            : "This item is not eligible for return, exchange, or replacement."}
        </p>
      );
    }

    const shouldShowExchangeNudge =
      rmsConfig.exchange_nudge_on_return_click && canExchange;

    const selectAction = (action) => {
      if (action === "return" && shouldShowExchangeNudge) {
        resetReasonDraft();
        setPendingIncentiviseSelection({
          product,
          returnType: "return",
          isReplacement: false,
          source: "inline",
        });
        setIncentiviseExchangeModalOpen(true);
        return;
      }
      const isReplacement = action === "replacement";
      handleOnSelect(
        action === "warranty"
          ? "warranty_claim"
          : isReplacement
            ? "exchange"
            : action,
        product,
        {
          isReplacement,
        },
      );
      setActiveLineAction(action);
      setActiveLineItemId(product.line_item_id);
    };

    if (
      rmsConfig.customer_flow_preference === "reason_first" &&
      !isWarrantyClaimFlow
    ) {
      return (
        <div className="action-selector">
          <Button
            variant="contained"
            color="primary"
            onClick={() => selectAction("reason_first")}
            disabled={!canExchange && !canReplace && !canReturn}
          >
            Choose reason
          </Button>
        </div>
      );
    }
    const returnIcon =
      rmsConfig.button_version === "v3" ||
      rmsConfig.button_version === "v4" ? null : (
        <ReplayIcon />
      );
    const returnLabel = rmsConfig.button_version !== "v2" ? "Return" : "";
    const useWideExchangeSplit =
      (rmsConfig.button_version === "v3" ||
        rmsConfig.button_version === "v4") &&
      canExchange &&
      canReturn &&
      !canReplace;
    const actionCount = [canExchange, canReplace, canReturn, canWarranty].filter(
      Boolean,
    ).length;
    const returnChargesStatement =
      rmsConfig.button_version === "v4" &&
      rmsConfig.base_refund_charge &&
      rmsConfig.base_refund_charge !== null
        ? `Extra ${rmsConfig.base_refund_charge}*`
        : null;

    return (
      <div className="action-selector">
        <p className="field-label">Action</p>
        <div
          className={`action-toggle ${
            useWideExchangeSplit && !canWarranty ? "wide-exchange-split" : ""
          } ${
            actionCount === 1 ? "single-action" : ""
          } ${
            actionCount === 3 ? "three-actions" : ""
          } ${
            !returnLabel && canExchange && canReturn && !canReplace
              ? "return-icon-only"
              : ""
          }`}
        >
          {canExchange && (
            <button
              type="button"
              className={activeAction === "exchange" ? "active" : ""}
              onClick={() => selectAction("exchange")}
            >
              <RepeatIcon />
              <span>Exchange</span>
            </button>
          )}
          {canReplace && (
            <button
              type="button"
              className={activeAction === "replacement" ? "active" : ""}
              onClick={() => selectAction("replacement")}
            >
              <RepeatIcon />
              <span>Replacement</span>
            </button>
          )}
          {canReturn && (
            <button
              type="button"
              className={`${activeAction === "return" ? "active" : ""} ${
                returnChargesStatement ? "has-charges" : ""
              }`}
              onClick={() => selectAction("return")}
              aria-label="Return"
            >
              {returnIcon}
              {returnLabel && <span>{returnLabel}</span>}
              {returnChargesStatement && (
                <span className="charges-statement">
                  {returnChargesStatement}
                </span>
              )}
            </button>
          )}
          {canWarranty && (
            <button
              type="button"
              className={activeAction === "warranty" ? "active" : ""}
              onClick={() => selectAction("warranty")}
            >
              <ShieldOutlinedIcon />
              <span>Claim Warranty</span>
            </button>
          )}
        </div>
        {rmsConfig.button_promotional_text &&
          rmsConfig.button_promotional_text !== "" && (
            <p
              className="incentivise-message"
              style={styles.incentiviseMessage}
            >
              {rmsConfig.button_promotional_text}
            </p>
          )}
      </div>
    );
  };

  const selectV2Product = (product) => {
    const warrantyEligible =
      isWarrantyClaimFlow && product?.warranty_claim_eligible === true;

    if (
      ((!product?.eligible && !isReplacementAvailable(product)) ||
        product.remaining_qty <= 0) &&
      !warrantyEligible
    ) {
      return;
    }

    if (activeLineItemId === product.line_item_id) {
      return;
    }

    if (activeLineItemId !== product.line_item_id) {
      resetReasonDraft();
    }
    const defaultQuantity = getDisplayQuantity(product) || 1;
    activeLineItemQuantityRef.current = {
      ...activeLineItemQuantityRef.current,
      [product.line_item_id]: defaultQuantity,
    };
    setActiveLineItemId(product.line_item_id);
    setActiveLineAction(null);
    setCanContinue(false);
    setReasonValidationAttempted(false);
    setSelectedLineCount(0);
    reasonSubmitRef.current = null;
    dispatch(addSelectedReason(null));
    dispatch(addSelectedSubReason(null));
    dispatch(setImageUploads([]));
    dispatch(addPreviews([]));
    dispatch(addUploads([]));
    dispatch(addProductType(null));
    dispatch(addProceedBool(false));
    dispatch(setReplacementMode(false));
  };

  const openConfiguredItemForEdit = (selectedReturnItem, product) => {
    const isReplacement = selectedReturnItem.return_type === "replacement";
    const isExchange = selectedReturnItem.return_type === "exchange";
    const action = selectedReturnItem.return_type === "warranty_claim"
      ? "warranty_claim"
      : isExchange || isReplacement ? "exchange" : "return";
    clearReasonDraft(getReasonDraftKey(product.line_item_id));
    handleOnSelect(
      action,
      product,
      { isReplacement },
    );
    setActiveLineItemId(product.line_item_id);
    setActiveLineAction(
      selectedReturnItem.return_type === "connect_to_agent" ? "connect_to_agent" : isReplacement ? "replacement" : action,
    );
    dispatch(
      addSelectedReason({
        reason_code: selectedReturnItem.customer_reason_code,
        reason_label: selectedReturnItem.customer_reason_label,
        reason_flow_type: selectedReturnItem.warranty_claim_flow_type,
        ...(selectedReturnItem.reason_mode === "reason_first" ? {
          reason_mode: "reason_first", selected_resolution: selectedReturnItem.return_type,
        } : {}),
        sub_reasons: [],
      }),
    );
    dispatch(
      addSelectedSubReason(
        selectedReturnItem.customer_sub_reason_code
          ? {
              reason_code: selectedReturnItem.customer_sub_reason_code,
              reason_label: selectedReturnItem.customer_sub_reason_label,
            }
          : null,
      ),
    );
    dispatch(
      addCustomerComment(selectedReturnItem.customer_reason_comment || ""),
    );
    const persistedMedia = selectedReturnItem.uploaded_media || [];
    dispatch(
      addUploads(
        persistedMedia.map(restorePersistedReturnMediaUpload),
      ),
    );
    dispatch(
      addPreviews(
        persistedMedia
          .map((mediaItem) => mediaItem.url)
          .filter((url) => typeof url === "string" && url.length > 0),
      ),
    );
    dispatch(
      addUploadMediaIds(
        updatedReturnedProducts
          .filter((item) => item.uploaded_media?.length)
          .map((item) => ({
            line_item_id: item.line_item_id,
            uploads: item.uploaded_media,
          })),
      ),
    );

    if (isExchange || isReplacement) {
      setExchangeSelectedVariant({
        variant_id: selectedReturnItem.exchange_variant_id,
        sku: selectedReturnItem.exchange_product_sku,
        price: selectedReturnItem.exchange_product_price,
        title: selectedReturnItem.exchange_variant_name,
        variant_img: selectedReturnItem.exchange_variant_image_url,
      });
      dispatch(addProductType(null));
    }
  };

  const removeConfiguredItem = (lineItemId) => {
    clearLineItemReasonDraft(lineItemId);
    const nextReturnedProducts = updatedReturnedProducts.filter(
      (item) => String(item.line_item_id) !== String(lineItemId),
    );
    dispatch(addReturnedProducts(nextReturnedProducts));
    setUpdatedReturnedProducts(nextReturnedProducts);
    if (String(selectedProductState?.line_item_id) === String(lineItemId)) {
      resetReasonDraft();
    }
    if (String(activeLineItemId) === String(lineItemId)) {
      collapseActiveLineItem();
    }
  };

  const cancelNewReasonSelection = (lineItemId) => {
    clearLineItemReasonDraft(lineItemId);
    resetReasonDraft();
    setActiveLineItemId(null);
    setActiveLineAction(null);
    setCanContinue(false);
    setReasonValidationAttempted(false);
    setSelectedLineCount(0);
    setReasonSubmitting(false);
    reasonSubmitRef.current = null;
  };

  const cancelConfiguredItemEdit = () => {
    clearReasonDraft(getReasonDraftKey(activeLineItemId));
    resetReasonDraft();
    setActiveLineItemId(null);
    setActiveLineAction(null);
    setCanContinue(false);
    setReasonValidationAttempted(false);
    setSelectedLineCount(0);
    setReasonSubmitting(false);
    reasonSubmitRef.current = null;
  };

  const renderProductCard = (product) => {
    const priceMarkup = renderProductPrice(product);
    const selectedReturnItem = updatedReturnedProducts.find(
      (item) => item.line_item_id === product.line_item_id,
    );
    const requestAlreadyCreated =
      !selectedReturnItem && isRequestAlreadyCreated(product);
    const trackingReferenceId = getLineItemReferenceId(product);
    const productEligible = isWarrantyClaimFlow
      ? product?.warranty_claim_eligible === true
      : selectedOrder?.eligible === false && !isReplacementAvailable(product)
        ? false
        : Boolean(product?.eligible || isReplacementAvailable(product));
    const itemUnavailableForNewRequest =
      !productEligible || requestAlreadyCreated;
    const canTrackExistingRequest =
      !selectedReturnItem &&
      itemUnavailableForNewRequest &&
      Boolean(trackingReferenceId);
    const isIneligible = itemUnavailableForNewRequest;
    const showDisabledOverlay = isIneligible && !canTrackExistingRequest;
    const ineligibleMessages = showDisabledOverlay
      ? getIneligibleMessages(product)
      : [];
    const isEditingConfiguredItem =
      !!selectedReturnItem && activeLineItemId === product.line_item_id;
    const isSelected =
      (activeLineItemId === product.line_item_id ||
        (product.line_item_id !== undefined &&
          productid !== null &&
          productid.includes(product.line_item_id))) &&
      (!selectedReturnItem || isEditingConfiguredItem);
    const activeAction =
      isSelected && activeLineItemId === product.line_item_id
        ? activeLineAction
        : null;
    const showQuantityEditor =
      isSelected && !selectedReturnItem && canEditLineItemQuantity(product);
    const replacementSummary =
      ["exchange", "replacement"].includes(selectedReturnItem?.return_type)
        ? {
            imageSrc: selectedReturnItem.exchange_variant_image_url,
            title: selectedReturnItem.exchange_product_title,
            variant: selectedReturnItem.exchange_variant_name,
            quantity: selectedReturnItem.quantity,
            price: selectedReturnItem.exchange_product_price
              ? `₹ ${selectedReturnItem.exchange_product_price}`
              : null,
          }
        : null;
    const cardActions =
      isSelected && (!selectedReturnItem || isEditingConfiguredItem) ? (
        <>
          {showQuantityEditor && renderLineItemQuantityEditor(product)}
          {!(rmsConfig.customer_flow_preference === "reason_first" && !isWarrantyClaimFlow && activeAction && activeAction !== "warranty") && (
            <ActionSelector product={product} activeAction={activeAction} />
          )}
          <ExpiryInfo {...getLineItemExpiryInfoProps(product)} />
          {activeAction && (
            <>
              <Reason
                onValidityChange={setCanContinue}
                onBusyChange={setReasonSubmitting}
                onSelectedCountChange={setSelectedLineCount}
                validationAttempted={reasonValidationAttempted}
                onValidationReset={resetReasonValidationFeedback}
                availableResolutions={[
                  "connect_to_agent",
                  ...(rmsConfig.supports_refund && !product.return_date_expired && product.refund_eligible ? ["refund"] : []),
                  ...(customerCanExchange && !product.exchange_date_expired && product.exchange_eligible ? ["exchange"] : []),
                  ...(isReplacementAvailable(product) ? ["replacement"] : []),
                ]}
                onResolutionSelect={(resolution) => {
                  if (!resolution) {
                    setActiveLineAction(null);
                    dispatch(setReplacementMode(false));
                    const nextProductId = `${product.line_item_id}return`;
                    setProductid(nextProductId);
                    dispatch(addProductid(nextProductId));
                    return;
                  }
                  setActiveLineAction(resolution);
                  dispatch(setReplacementMode(resolution === "replacement"));
                  const productType = ["exchange", "replacement"].includes(resolution) ? "exchange" : "return";
                  const nextProductId = `${product.line_item_id}${productType}`;
                  setProductid(nextProductId);
                  dispatch(addProductid(nextProductId));
                }}
                registerSubmit={(submit) => {
                  reasonSubmitRef.current = submit;
                }}
              />
              {productTagConsentText && (
                <p className="line-item-consent-note">
                  <strong>Note:</strong> {productTagConsentText}
                </p>
              )}
              <div
                className={`reason-action-row ${
                  isEditingConfiguredItem ? "editing" : ""
                }`}
              >
                <Button
                  variant="outlined"
                  className="reason-cancel-button"
                  onClick={() =>
                    isEditingConfiguredItem
                      ? cancelConfiguredItemEdit()
                      : cancelNewReasonSelection(product.line_item_id)
                  }
                  disabled={reasonSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={reasonSubmitting}
                  aria-invalid={!canContinue}
                  className={`reason-continue-button ${
                    reasonButtonErrorShake ? "shake-error" : ""
                  } ${reasonSubmitting ? "loading" : ""}`}
                  onAnimationEnd={() => setReasonButtonErrorShake(false)}
                  onClick={async () => {
                    setReasonValidationAttempted(false);
                    requestAnimationFrame(() =>
                      setReasonValidationAttempted(true),
                    );
                    if (!reasonSubmitRef.current) {
                      setReasonButtonErrorShake(false);
                      requestAnimationFrame(() =>
                        setReasonButtonErrorShake(true),
                      );
                      return;
                    }
                    setReasonSubmitting(true);
                    const result = await reasonSubmitRef.current();
                    setReasonSubmitting(false);
                    if (!result) {
                      setReasonButtonErrorShake(false);
                      requestAnimationFrame(() =>
                        setReasonButtonErrorShake(true),
                      );
                      return;
                    }
                    setReasonButtonErrorShake(false);
                    setReasonValidationAttempted(false);
                    let productsForCart = updatedReturnedProducts;
                    if (Array.isArray(result)) {
                      productsForCart = result;
                    }
                    if (
                      activeLineAction === "exchange" ||
                      activeLineAction === "replacement"
                    ) {
                      collapseActiveLineItem();
                      dispatch(addProductType(null));
                      navigateProductsStep(
                        activeLineAction === "replacement" ? "same" : "type",
                      );
                      return;
                    }
                    if (productsForCart.length === 0) {
                      return;
                    }
                    if (activeLineAction === "warranty") {
                      collapseActiveLineItem();
                      navigateReturnCart(productsForCart, {
                        warrantyClaim: true,
                      });
                      return;
                    }
                    collapseActiveLineItem();
                    handlePostSelectionNavigation(productsForCart);
                  }}
                >
                  {reasonSubmitting ? (
                    <>
                      <CircularProgress
                        size={16}
                        thickness={5}
                        className="reason-button-spinner"
                      />
                      <span>Processing...</span>
                    </>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </div>
            </>
          )}
        </>
      ) : null;

    return (
      <LineItemCard
        product={product}
        imageSrc={product.image_url}
        title={product.product_title}
        variant={product.variant_name}
        quantity={
          getDisplayQuantity(product) !== undefined && !showQuantityEditor
            ? getLineItemQuantity(product)
            : undefined
        }
        price={priceMarkup}
        replacement={!isEditingConfiguredItem ? replacementSummary : null}
        statusLabel={
          selectedReturnItem
            ? selectedReturnItem.return_type === "connect_to_agent"
              ? "Connect to Agent"
              : isWarrantyClaimFlow
              ? "Claim Warranty"
              : ["exchange", "replacement"].includes(
                  selectedReturnItem.return_type,
                )
              ? selectedReturnItem.return_type === "replacement"
                ? "Replacement"
                : "Exchange"
              : "Refund"
            : null
        }
        selected={isSelected}
        configured={Boolean(selectedReturnItem)}
        ineligible={showDisabledOverlay}
        requestCreated={canTrackExistingRequest}
        showSelectIndicator={!selectedReturnItem && !isIneligible}
        showTrackRequest={canTrackExistingRequest}
        showEdit={Boolean(selectedReturnItem && !isEditingConfiguredItem)}
        showDelete={Boolean(selectedReturnItem && !isEditingConfiguredItem)}
        ineligibleMessages={ineligibleMessages}
        actions={cardActions}
        actionsClassName={!activeAction ? "line-item-actions-enter" : ""}
        primaryColor={merchantConfig.primary_theme_color}
        rounded={merchantConfig.use_rounded_edge_buttons}
        onClick={(event) => {
          const target = event.target;
          if (
            target?.closest?.(".line-item-actions") ||
            target?.closest?.(".reason-select") ||
            target?.closest?.(".reason-select-menu")
          ) {
            return;
          }
          selectV2Product(product);
        }}
        onEdit={() => {
          if (isEditingConfiguredItem) {
            cancelConfiguredItemEdit();
            return;
          }
          openConfiguredItemForEdit(selectedReturnItem, product);
        }}
        onDelete={() => {
          removeConfiguredItem(product.line_item_id);
        }}
        onTrackRequest={() => openTrackingRequest(trackingReferenceId)}
      />
    );
  };

  const RequestReferenceGroupCard = ({ referenceId, items }) => {
    if (!referenceId || !Array.isArray(items) || items.length === 0) {
      return null;
    }

    return (
      <div
        className="request-reference-card"
        style={{
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px"
            : "0px",
        }}
      >
        <div className="request-reference-items">
          {items.map((item) => {
            const priceMarkup = renderProductPrice(item);

            return (
              <div
                className="request-reference-item"
                key={item.line_item_id || `${referenceId}-${item.product_id}`}
              >
                <img
                  src={item.image_url}
                  alt={item.product_title || ""}
                  className="request-reference-image"
                />
                <div className="request-reference-details">
                  <p className="request-reference-title">
                    {item.product_title}
                  </p>
                  {item.variant_name ? (
                    <p className="request-reference-variant">
                      {item.variant_name}
                    </p>
                  ) : null}
                  <div className="request-reference-meta">
                    <span>Qty: {getLineItemQuantity(item)}</span>
                    {priceMarkup ? <span>{priceMarkup}</span> : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="request-reference-action">
          <button
            type="button"
            className="request-reference-track-button"
            style={{
              borderColor: merchantConfig.primary_theme_color,
              color: merchantConfig.primary_theme_color,
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "10px"
                : "0px",
            }}
            onClick={() => openTrackingRequest(referenceId)}
          >
            <span>Track Request</span>
            <ArrowOutwardIcon />
          </button>
        </div>
      </div>
    );
  };

  const ProductGroupCard = ({ group }) => {
    const parent = group.parent;
    const children = group.children || [];

    const aggregatedProduct = useMemo(
      () => aggregateGroupProduct(parent, children),
      [parent, children],
    );

    const parentTitle =
      parent?.product_title ||
      parent?.parent_title ||
      children[0]?.parent_title ||
      children[0]?.product_title ||
      "";

    const parentVariant =
      parent?.variant_name ||
      parent?.parent_variant ||
      children[0]?.parent_variant ||
      "";

    const parentQuantity =
      parent?.remaining_qty && parent.remaining_qty > 0
        ? parent.remaining_qty
        : parent?.quantity ||
          parent?.parent_quantity ||
          children[0]?.parent_quantity;

    const parentPriceMarkup = renderProductPrice(parent);

    const parentImage =
      parent?.parent_image_url ||
      parent?.image_url ||
      children.find((child) => child.parent_image_url)?.parent_image_url ||
      children.find((child) => child.image_url)?.image_url ||
      "";

    const isAnyChildSelected = children.some(
      (child) =>
        child.line_item_id !== undefined &&
        productid !== null &&
        productid.includes(child.line_item_id),
    );

    const isExpanded = expandedGroupKey === group.key;
    const actionType = isExpanded ? activeGroupAction : null;
    const isReplacementFlow = isExpanded ? isReplacementAction : false;
    const actionLabel = isReplacementFlow
      ? "replace"
      : actionType === "return"
        ? "return"
        : actionType === "exchange"
          ? "exchange"
          : actionType === "reason_first"
            ? "request"
          : null;

    const childrenContainerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const initialRenderRef = useRef(true);
    const groupContainerRef = useRef(null);

    useEffect(() => {
      const element = childrenContainerRef.current;
      if (!element) {
        return;
      }

      const duration = 280;
      const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

      const animate = (startHeight, endHeight) => {
        let startTime = null;

        const step = (timestamp) => {
          if (!startTime) {
            startTime = timestamp;
          }

          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeInOut(progress);
          const height = startHeight + (endHeight - startHeight) * eased;

          element.style.height = `${height}px`;

          if (progress < 1) {
            animationFrameRef.current = requestAnimationFrame(step);
          } else {
            if (endHeight === 0) {
              element.style.display = "none";
              element.style.height = "0px";
              element.style.overflow = "hidden";
            } else {
              element.style.height = "auto";
              element.style.overflow = "visible";
            }
          }
        };

        animationFrameRef.current = requestAnimationFrame(step);
      };

      cancelAnimationFrame(animationFrameRef.current);

      if (initialRenderRef.current) {
        initialRenderRef.current = false;
        if (isExpanded) {
          element.style.display = "flex";
          element.style.height = "auto";
          element.style.overflow = "visible";
        } else {
          element.style.display = "none";
          element.style.height = "0px";
          element.style.overflow = "hidden";
        }
        return;
      }

      if (isExpanded) {
        element.style.display = "flex";
        element.style.overflow = "hidden";
        element.style.height = "auto";
        const targetHeight = element.scrollHeight;
        element.style.height = "0px";
        animate(0, targetHeight);
      } else {
        const currentHeight = element.scrollHeight;
        if (currentHeight === 0) {
          element.style.display = "none";
          element.style.height = "0px";
          element.style.overflow = "hidden";
        } else {
          element.style.overflow = "hidden";
          animate(currentHeight, 0);
        }
      }

      return () => {
        cancelAnimationFrame(animationFrameRef.current);
      };
    }, [isExpanded]);

    useEffect(() => {
      const element = childrenContainerRef.current;
      if (element && isExpanded) {
        element.style.display = "flex";
        element.style.height = "auto";
        element.style.overflow = "visible";
      }
    }, [isExpanded, children.length, actionType, isReplacementFlow]);

    useEffect(() => {
      if (!isExpanded || initialRenderRef.current) {
        return;
      }

      const element = groupContainerRef.current;
      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const scrollOffset = window.innerWidth > 900 ? 120 : 80;
      const target = window.pageYOffset + rect.top - scrollOffset;

      window.scrollTo({
        top: target > 0 ? target : 0,
        behavior: "smooth",
      });
    }, [isExpanded]);

    const selectedActionForButtons =
      isExpanded && actionType
        ? { type: actionType, isReplacement: isReplacementFlow }
        : null;

    return (
      <StyledComponentGroup
        primary_color={merchantConfig.primary_theme_color}
        className={isAnyChildSelected ? "selected" : ""}
        rounded={merchantConfig.use_rounded_edge_buttons}
        ref={groupContainerRef}
      >
        <div className="component-parent">
          <LazyLoadImage
            effect="opacity"
            src={parentImage}
            alt={parentTitle}
            className="component-parent-image"
          />
          <div className="product-details">
            <p
              style={{
                color: "var(--rms-v2-text-body)",
                maxHeight: "3em",
                overflow: "hidden",
              }}
            >
              {parentTitle}
            </p>
            {parentVariant && (
              <p
                className="margin"
                style={{ color: "var(--rms-v2-text-muted)" }}
              >
                {parentVariant}
              </p>
            )}
            <div style={{ display: "flex" }}>
              {parentQuantity !== undefined && parentQuantity !== null && (
                <p
                  className="margin"
                  style={{ color: "var(--rms-v2-text-muted)" }}
                >
                  Quantity: {parentQuantity}
                </p>
              )}
              {parentPriceMarkup && (
                <p
                  className="margin"
                  style={{
                    color: "var(--rms-v2-text-muted)",
                    fontWeight: "600",
                    marginLeft: "auto",
                  }}
                >
                  {parentPriceMarkup}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="component-action-buttons">
          <RefundButtons
            product={aggregatedProduct}
            onActionClick={(action, options) =>
              handleGroupActionSelection(group.key, action, options)
            }
            selectedAction={selectedActionForButtons}
          />
        </div>
        <div ref={childrenContainerRef} className="component-children">
          {actionLabel && (
            <p className="component-child-message">
              Select the item you want to {actionLabel}.
            </p>
          )}
          {children.map((child) => {
            const currentProduct = selectedOrder.order_items?.find(
              (a) => a.line_item_id === child.line_item_id,
            );

            const showBanner =
              currentProduct &&
              (currentProduct.quantity - child.quantity > 0 ||
                child.returned_qty > 0) &&
              child.remaining_qty > 0;

            const addedQuantity = currentProduct
              ? currentProduct.quantity - child.quantity > 0
                ? currentProduct.quantity - child.quantity
                : child.returned_qty
              : child.returned_qty;

            const childSelected =
              child.line_item_id !== undefined &&
              productid !== null &&
              productid.includes(child.line_item_id);

            const childPriceMarkup = renderProductPrice(child);
            const childImage = child.image_url || parentImage;

            const { selectable, message, referenceId } = getChildActionState(
              child,
              actionType,
              isReplacementFlow,
            );
            const isSelectable = Boolean(actionType) && selectable;

            const childClasses = [
              "component-child",
              childSelected ? "selected" : "",
              isExpanded ? (isSelectable ? "selectable" : "disabled") : "",
            ]
              .filter(Boolean)
              .join(" ");

            const handleChildSelection = () => {
              if (!isSelectable) {
                return;
              }
              if (actionType === "return") {
                handleOnSelect("return", child, { isReplacement: false });
              } else if (actionType === "exchange") {
                handleOnSelect("exchange", child, {
                  isReplacement: isReplacementFlow,
                });
              } else if (actionType === "reason_first") {
                handleOnSelect("reason_first", child);
                setActiveLineAction("reason_first");
                setActiveLineItemId(child.line_item_id);
              }
              setIncentiviseExchangeModalOpen(false);
            };

            return (
              <div
                key={child.line_item_id}
                className={childClasses}
                role={isSelectable ? "button" : "group"}
                tabIndex={isSelectable ? 0 : -1}
                aria-disabled={!isSelectable}
                onClick={handleChildSelection}
                onKeyDown={(event) => {
                  if (!isSelectable) {
                    return;
                  }
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleChildSelection();
                  }
                }}
              >
                {showBanner && addedQuantity > 0 && (
                  <p className="top-banner">
                    {addedQuantity} qty added to return cart
                  </p>
                )}
                <div className="child-body">
                  <LazyLoadImage
                    effect="opacity"
                    src={childImage}
                    alt={child.product_title}
                    className="child-image"
                  />
                  <div className="child-info">
                    <p
                      className="title"
                      style={{
                        color: "var(--rms-v2-text-body)",
                        maxHeight: "3em",
                        overflow: "hidden",
                      }}
                    >
                      {child.product_title}
                    </p>
                    {child.variant_name && (
                      <p className="margin">{child.variant_name}</p>
                    )}
                    <div style={{ display: "flex" }}>
                      <p className="margin">
                        Quantity:{" "}
                        {child.remaining_qty > 0
                          ? child.remaining_qty
                          : child.quantity}
                      </p>
                      {/* {childPriceMarkup && (
                        <p
                          className="margin price"
                          style={{ marginLeft: "auto" }}
                        >
                          {childPriceMarkup}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                {isExpanded && !isSelectable && message && (
                  <>
                    <p className="info-message">{message}</p>
                    {renderTrackRequestButton(referenceId)}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </StyledComponentGroup>
    );
  };


  const reasonExchangeConfig = getEffectiveResolutionConfig(
    selectedReasonState,
    selectedSubReasonState,
    "exchange",
  );
  const orderAllowsSameExchange =
    selectedOrder?.exchange_configurations?.length === 0 ||
    selectedOrder?.exchange_configurations?.includes("same_variant_exchange");
  const orderAllowsCatalogExchange =
    selectedOrder?.exchange_configurations?.length === 0 ||
    selectedOrder?.exchange_configurations?.includes("diff_product_variant");
  const canUseSameExchange = isReplacement || (
    (selectedReasonState?.reason_mode !== "reason_first" ||
      reasonExchangeConfig?.same_product_variants !== false) &&
    orderAllowsSameExchange
  );
  const canUseDifferentExchange =
    rmsConfig.exchange_with_any_product &&
    (selectedReasonState?.reason_mode !== "reason_first" ||
      reasonExchangeConfig?.any_catalog_product === true) &&
    orderAllowsCatalogExchange;

  const renderExchangeSummary = () => {
    if (!selectedProductState) {
      return null;
    }

    return (
      <div className="exchange-item-summary">
        <img
          src={selectedProductState.image_url}
          alt={selectedProductState.product_title}
        />
        <div>
          <p className="exchange-summary-label">Exchanging</p>
          <p className="exchange-summary-title">
            {selectedProductState.product_title}
          </p>
          <div className="exchange-container">
            <p className="exchange-variant-title">
              {selectedProductState.variant_name}
            </p>
            <p className="exchange-summary-price">
              {renderProductPrice(selectedProductState)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderExchangeTypeScreen = () => {
    const isSame = selectedProductType === "same";
    const isDiff = selectedProductType === "different";

    return (
      <div className="exchange-type-screen">
        <PageIntro
          title="Choose Exchange Type"
          description="Select how you'd like to proceed with your exchange."
          padding="0"
          margin="0 0 12px"
        />
        {renderExchangeSummary()}
        <div className="exchange-type-options">
          {canUseSameExchange && (
            <div
              className={`exchange-type-card ${isSame ? "selected" : ""}`}
            >
              <button
                type="button"
                onClick={() => {
                  dispatch(addProductType("same"));
                  setExchangeRequiredProduct(null);
                  setExchangeOpenFilter(false);
                }}
                className="exchange-type-option-button"
              >
                <div>
                  <p className="exchange-type-option-title">
                    {rmsConfig.same_variant_option_title ||
                      "Exchange with same product"}
                  </p>
                  <p className="exchange-type-option-description">
                    Switch size or color for same model
                  </p>
                </div>
                <CheckmarkControl
                  className="exchange-type-check"
                  primaryColor={merchantConfig.primary_theme_color}
                  selected={isSame}
                  size={18}
                  style={{ marginTop: "2px" }}
                />
              </button>
              {isSame && (
                <div className="same-exchange-panel">
                  <div className="same-exchange-panel-body">
                    <SameProduct
                      quantityInc={
                        selectedProductState?.remaining_qty > 0
                          ? selectedProductState.remaining_qty
                          : selectedProductState?.quantity
                      }
                      productType="same"
                      selectedVariant={exchangeSelectedVariant}
                      setSelectedVariant={setExchangeSelectedVariant}
                      otherReason={customerComment}
                      setAnimate={() => {}}
                      setUploads={() => {}}
                      showInternalContinue={false}
                      registerSameSubmit={(submit) => {
                        sameExchangeSubmitRef.current = submit;
                      }}
                      onExchangeComplete={handlePostSelectionNavigation}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {canUseDifferentExchange && (
            <button
              type="button"
              onClick={() => {
                dispatch(addProductType("different"));
              }}
              className={`exchange-type-card exchange-type-card-button ${
                isDiff ? "selected" : ""
              }`}
            >
              <div>
                <p className="exchange-type-option-title">
                  {rmsConfig.other_product_option_title ||
                    "Exchange with different product"}
                </p>
                <p className="exchange-type-option-description">
                  Browse collection to find perfect alternative
                </p>
              </div>
              <CheckmarkControl
                className="exchange-type-check"
                primaryColor={merchantConfig.primary_theme_color}
                selected={isDiff}
                size={18}
                style={{ marginTop: "2px" }}
              />
            </button>
          )}
        </div>

        <BottomActionBar
          actionLabel="Continue"
          showCount={false}
          rounded={merchantConfig.use_rounded_edge_buttons}
          disabled={!selectedProductType}
          buttonProps={{
            className: "exchange-type-continue-button",
          }}
          onAction={async () => {
            if (!selectedProductType) {
              return;
            }

            if (selectedProductType === "same") {
              if (!sameExchangeSubmitRef.current) {
                return;
              }
              await sameExchangeSubmitRef.current();
              return;
            }

            navigateProductsStep(selectedProductType);
          }}
        />
      </div>
    );
  };

  const renderSameExchangeScreen = () => (
    <div className="same-exchange-direct-screen">
      <SameProduct
        quantityInc={
          selectedProductState?.remaining_qty > 0
            ? selectedProductState.remaining_qty
            : selectedProductState?.quantity
        }
        productType="same"
        selectedVariant={exchangeSelectedVariant}
        setSelectedVariant={setExchangeSelectedVariant}
        otherReason={customerComment}
        setAnimate={() => {}}
        setUploads={() => {}}
        showInternalContinue={!isReplacement}
        registerSameSubmit={(submit) => {
          sameExchangeSubmitRef.current = submit;
        }}
        onExchangeComplete={handlePostSelectionNavigation}
      />
    </div>
  );

  const renderProductsBottomActionBar = () => {
    const selectedCount = selectedLineCount || updatedReturnedProducts.length;

    return (
      <BottomActionBar
        actionLabel="Continue"
        count={selectedCount}
        rounded={merchantConfig.use_rounded_edge_buttons}
        disabled={reasonSubmitting || updatedReturnedProducts.length === 0}
        onAction={async () => {
          navigateReturnCart(updatedReturnedProducts);
        }}
      />
    );
  };

  const renderExchangeFlow = () => {
    return (
      <div className="products-wrapper">
        <PageHeader
          logoSrc={merchantConfig.brand_logo}
          logoAlt={merchantConfig.shop_display_name}
          primaryColor={merchantConfig.primary_theme_color}
          onBack={() => {
            if (returnFlowDeepLink) {
              navigateToOrderSummary();
              return;
            }

            if (exchangeStep === "different" && !isReplacement) {
              setExchangeSelectedVariant(null);
              setExchangeRequiredProduct(null);
              setExchangeOpenFilter(false);
              navigateProductsStep("type");
              return;
            }
            if (selectedProductState?.line_item_id) {
              setActiveLineItemId(selectedProductState.line_item_id);
              setActiveLineAction(isReplacement ? "replacement" : "exchange");
              setCanContinue(false);
              setReasonValidationAttempted(false);
              setReasonSubmitting(false);
              setSelectedLineCount(
                selectedProductState.remaining_qty ||
                  selectedProductState.quantity ||
                  0,
              );
              reasonSubmitRef.current = null;
              dispatch(
                addProductid(`${selectedProductState.line_item_id}exchange`),
              );
              setProductid(`${selectedProductState.line_item_id}exchange`);
            }
            dispatch(addProductType(null));
            navigateProductsStep(null);
          }}
        />
        <div className="exchange-flow-content">
          {exchangeStep === "type" && renderExchangeTypeScreen()}
          {exchangeStep === "same" && renderSameExchangeScreen()}
          {exchangeStep === "different" && (
            <>
              <PageIntro
                title="Choose Replacement"
                description="Search and select a replacement product."
                padding="0"
                margin="0 0 1rem"
              />
              {renderExchangeSummary()}
              <DifferentProduct
                quantityInc={
                  selectedProductState?.remaining_qty > 0
                    ? selectedProductState.remaining_qty
                    : selectedProductState?.quantity
                }
                productType="different"
                requiredProduct={exchangeRequiredProduct}
                setRequiredProduct={setExchangeRequiredProduct}
                selectedVariant={exchangeSelectedVariant}
                setSelectedVariant={setExchangeSelectedVariant}
                otherReason={customerComment}
                setAnimate={() => {}}
                openFilter={exchangeOpenFilter}
                setOpenFilter={setExchangeOpenFilter}
                setUploads={() => {}}
                actionLabel="Confirm Selection"
                pageMode
                onExchangeComplete={handlePostSelectionNavigation}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <StyledApp>
      {apiErrorLoading ? (
        <LoadingError minHeight="80vh" />
      ) : (
        <StyledProductsWrapper
          location={currentPath}
          returnedProducts={returnedProducts}
          primary_color={merchantConfig.primary_theme_color}
          rounded={merchantConfig.use_rounded_edge_buttons}
        >
          {productsLoading ? (
            <V2ProductsBoneyardLoader />
          ) : isV2ExchangeFlow ? (
            renderExchangeFlow()
          ) : (
            <div className="products-wrapper">
              <PageHeader
                logoSrc={merchantConfig.brand_logo}
                logoAlt={merchantConfig.shop_display_name}
                primaryColor={merchantConfig.primary_theme_color}
                onBack={navigateToOrderSummary}
              />
              {currentPath === "/products" && (
                <div>
                  {selectedOrder.order_items.length === 0 ? (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        margin: "1.5rem 0",
                        maxWidth: "700px",
                      }}
                    >
                      {/* <img src={cartImage} style={{ height: "100px" }} /> */}
                      <p
                        style={{
                          padding: "1rem",
                          fontWeight: "700",
                          textAlign: "center",
                          fontSize: "16px",
                          color: "var(--rms-v2-text-body)",
                        }}
                      >
                        No other items found for this order
                      </p>
                    </div>
                  ) : selectedOrder.order_items.length === 0 &&
                    updatedReturnedProducts.length > 0 ? (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        margin: "1.5rem 0",
                        maxWidth: "700px",
                      }}
                    >
                      <p
                        style={{
                          padding: "1rem",
                          fontWeight: "700",
                          textAlign: "center",
                          fontSize: "16px",
                          color: "var(--rms-v2-text-body)",
                        }}
                      >
                        {isWarrantyClaimFlow ? "All eligible warranty/service items have been added." : "All eligible return items have been added."}
                      </p>
                    </div>
                  ) : (
                    <PageIntro
                      title={`Order ${
                        selectedOrder?.order_name?.startsWith("#")
                          ? selectedOrder.order_name
                          : `#${selectedOrder?.order_name || selectedOrder?.order_number || ""}`
                      }`}
                      description={isWarrantyClaimFlow ? "Choose the item you need warranty or repair support for." : "Choose the items you wish to return or exchange."}
                      className="select-products"
                      padding="10px 20px"
                    />
                  )}
                </div>
              )}
              <Grid
                direction="column"
                container
                rowSpacing={{ xs: 2 }}
                className="products-grid"
              >
                {eligibleDisplayItems.map((entry) => {
                  if (entry.type === "group") {
                    return (
                      <Grid
                        key={entry.key}
                        justifyContent="center"
                        item
                        xs={12}
                      >
                        <ProductGroupCard group={entry} />
                      </Grid>
                    );
                  }

                  if (!entry.item) {
                    return null;
                  }

                  return (
                    <Grid
                      key={entry.item.line_item_id}
                      justifyContent="center"
                      item
                      xs={12}
                    >
                      {renderProductCard(entry.item)}
                    </Grid>
                  );
                })}

                {requestGroupedIneligibleItems.map((entry) => {
                  if (entry.type === "request-reference-group") {
                    if (entry.items.length === 1) {
                      return (
                        <Grid
                          key={`ineligible-${entry.items[0].line_item_id}`}
                          justifyContent="center"
                          item
                          xs={12}
                        >
                          {renderProductCard(entry.items[0])}
                        </Grid>
                      );
                    }

                    return (
                      <Grid
                        key={entry.key}
                        justifyContent="center"
                        item
                        xs={12}
                      >
                        <RequestReferenceGroupCard
                          referenceId={entry.referenceId}
                          items={entry.items}
                        />
                      </Grid>
                    );
                  }

                  if (entry.type === "group") {
                    return (
                      <Grid
                        key={`ineligible-${entry.key}`}
                        justifyContent="center"
                        item
                        xs={12}
                      >
                        <ProductGroupCard group={entry} />
                      </Grid>
                    );
                  }

                  if (!entry.item) {
                    return null;
                  }

                  return (
                    <Grid
                      key={`ineligible-${entry.item.line_item_id}`}
                      justifyContent="center"
                      item
                      xs={12}
                    >
                      {renderProductCard(entry.item)}
                    </Grid>
                  );
                })}
              </Grid>
              {/* {location.pathname === "/products" && (
                <p
                  onClick={() => navigate(`/?encipherencode=${encipherencode}`)}
                  className="change-user"
                >
                  Change Phone/Email ?
                </p>
              )} */}
              {/* {location.pathname === "/products" &&
                merchantConfig.whatsapp_help &&
                merchantConfig.whatsapp_help !== null && (
                  <a
                    href={merchantConfig.whatsapp_help}
                    target="_blank"
                    rel="noreferrer"
                    className="help"
                    style={{
                      fontWeight: "600",
                      fontSize: "12px",
                      color: "var(--color-primary)",
                      margin: "60px 0px",
                      textDecoration: "underline",
                      display: "block",
                    }}
                  >
                    Need Help?
                  </a>
                )} */}

              {!activeLineItemId &&
                updatedReturnedProducts.length > 0 &&
                renderProductsBottomActionBar()}
            </div>
          )}
        </StyledProductsWrapper>
      )}

      <BottomSheetDrawer
        aria-labelledby="Incentvise Exchange"
        aria-describedby="Incentivising Exchange over Return of a product"
        open={
          incentiviseExchangeModalOpen &&
          customerCanExchange &&
          !pendingIncentiviseSelection?.product?.exchange_date_expired &&
          pendingIncentiviseSelection?.product?.exchange_eligible
        }
        onClose={() => {
          setIncentiviseExchangeModalOpen(false);
          setPendingIncentiviseSelection(null);
        }}
        transitionDuration={260}
        rounded={merchantConfig.use_rounded_edge_buttons}
        paperClassName="nudge-bottom-sheet"
        paperSx={{
            left: 0,
            right: 0,
            margin: "0 auto",
            width: "100%",
            maxWidth: "500px",
            overflow: "visible",
            background: "var(--rms-v2-surface)",
            "& .nudge-sheet-content": {
              position: "relative",
              padding: "10px 22px 28px",
              boxSizing: "border-box",
            },
            "& .bottom-sheet-pull": {
              display: "block",
              width: "42px",
              height: "5px",
              borderRadius: "999px",
              background: "var(--rms-v2-border)",
              margin: "0 auto 18px",
            },
            "& .nudge-body": {
              textAlign: "center",
              paddingTop: "8px",
            },
            "& .nudge-title": {
              margin: "10px 0px 16px",
              color: "var(--rms-v2-text-title)",
              fontSize: "16px",
              fontWeight: 900,
            },
            "& .nudge-description": {
              margin: 0,
              color: "var(--rms-v2-text-muted)",
              fontSize: "14px",
            },
            "& .nudge-actions": {
              marginTop: "22px",
            },
            "& .nudge-return-link": {
              margin: "12px 0 0",
              color: merchantConfig.primary_theme_color,
              fontSize: "13px",
              fontWeight: 800,
              cursor: "pointer",
            },
        }}
      >
        <div className="nudge-sheet-content">
          <span className="bottom-sheet-pull" />
          <FloatingCloseButton
            ariaLabel="Close exchange prompt"
            className="nudge-close"
            onClick={() => {
              setIncentiviseExchangeModalOpen(false);
              setPendingIncentiviseSelection(null);
            }}
          />
          <div className="nudge-body">
            <p className="nudge-title">{rmsConfig.exchange_nudge_title}</p>
            <p className="nudge-description">
              {rmsConfig.exchange_nudge_description}
            </p>
            <div className="nudge-actions">
              {customerCanExchange &&
                !pendingIncentiviseSelection?.product?.exchange_date_expired &&
                pendingIncentiviseSelection?.product?.exchange_eligible &&
                renderButton(
                  "Exchange",
                  () => {
                    if (!pendingIncentiviseSelection?.product) {
                      return;
                    }
                    openReasonFlow(
                      "exchange",
                      pendingIncentiviseSelection.product,
                      {
                        isReplacement: false,
                      },
                    );
                  },
                  !pendingIncentiviseSelection?.product?.exchange_eligible,
                  <RepeatIcon />,
                  {
                    width: "100%",
                    background: merchantConfig.primary_theme_color,
                    color: merchantConfig.secondary_theme_color,
                  },
                  null,
                  true,
                )}
              <p
                className="nudge-return-link"
                onClick={() => {
                  if (!pendingIncentiviseSelection?.product) {
                    setIncentiviseExchangeModalOpen(false);
                    setPendingIncentiviseSelection(null);
                    return;
                  }
                  openReasonFlow(
                    pendingIncentiviseSelection.returnType || "return",
                    pendingIncentiviseSelection.product,
                    {
                      isReplacement:
                        pendingIncentiviseSelection.isReplacement || false,
                    },
                  );
                }}
              >
                No, thanks! I'll proceed with the return.
              </p>
            </div>
          </div>
        </div>
      </BottomSheetDrawer>
    </StyledApp>
  );
}

export default ProductsDisplay;
